if (!customElements.get('ktechs-builder-v2')) {
  class KtechsBuilderV2 extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 0;
      this.layout = null;
      this.selections = {};
      this.layoutLabels = {};
      this.started = false;
      this.isHydrating = true;
      this.swFeel = 'all';
      this.shown = {};
      this.reveal = {};
      this.undoSnapshot = null;
      this.focusKey = null;
      this.toastTimer = null;
      this.toastDismiss = null;
      this.pendingSavedRestore = null;
      this.drawerReturnFocus = null;
      this.handleThemeBlockSelect = this.handleThemeBlockSelect.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      try {
        this.strings = JSON.parse(this.querySelector('[data-builder-strings]')?.textContent || '{}');
      } catch (_error) {
        this.strings = {};
      }
      this.pageSize = Math.max(1, Number(this.dataset.pageSize || 12));
      this.minSpares = Math.max(0, Number(this.dataset.minSpares || 0));
      this.b2b = {
        mode: this.dataset.b2bMode === 'package' ? 'package' : 'retail',
        client: (this.dataset.b2bClient || '').trim(),
        subsidy: Math.max(0, Number(this.dataset.b2bSubsidy || 0)),
        cap: Math.max(0, Number(this.dataset.b2bCap || 0)),
        capBehavior: this.dataset.b2bCapBehavior === 'block' ? 'block' : 'topup'
      };

      this.stepNav = this.querySelector('[data-builder-step-nav]');
      this.nextButton = this.querySelector('[data-builder-next]');
      this.backButton = this.querySelector('[data-builder-back]');
      this.errorBox = this.querySelector('[data-builder-error]');
      this.sheet = this.querySelector('[data-builder-sheet]');

      this.stepDefinitions = this.getStepDefinitions();
      this.assemblyDefinition = this.stepDefinitions.find((definition) => definition.assembly) || null;

      this.stepDefinitions.forEach((definition) => {
        if (definition.key !== 'layout' && !definition.review) {
          this.selections[definition.key] = definition.selectionMode === 'multiple' ? [] : null;
          this.shown[definition.key] = this.pageSize;
          this.reveal[definition.key] = false;
        }
      });

      this.layoutButtons = Array.from(this.querySelectorAll('[data-builder-layout]'));
      this.layoutButtons.forEach((button) => {
        const key = button.dataset.builderLayout?.trim();
        if (key) this.layoutLabels[key] = button.dataset.layoutLabel || key;
        button.addEventListener('click', () => this.selectLayout(key));
      });

      // keyboard sources authored inside Layout option blocks: adopt them into
      // the Keyboard step's grid (their key comes from the layout block itself,
      // so the pairing can never mismatch)
      const keyboardStepDefinition = this.stepDefinitions.find((definition) => definition.key === 'keyboard');
      const keyboardGrid = keyboardStepDefinition?.block.querySelector('.ktechs-builder__cards');
      if (keyboardGrid) {
        this.querySelectorAll('[data-builder-layout-keyboards]').forEach((wrap) => keyboardGrid.appendChild(wrap));
      }

      // hand-picked products (Keycap/Switch/Add-on/Service item blocks) lead
      // their grid — rendered after a full collection they would otherwise
      // hide behind “Show more”
      this.querySelectorAll('.ktechs-builder__cards').forEach((grid) => {
        Array.from(grid.querySelectorAll(':scope > .ktechs-builder__theme-item'))
          .reverse()
          .forEach((wrap) => grid.prepend(wrap));
      });

      this.querySelectorAll('[data-builder-keyboard-source]').forEach((source) => {
        source.querySelectorAll('[data-builder-item][data-component="keyboard"]').forEach((card) => {
          card.dataset.layoutKey = source.dataset.layoutKey || card.dataset.layoutKey;
        });
      });
      this.switchCategories = [];
      this.querySelectorAll('[data-builder-switch-source]').forEach((source) => {
        source.hidden = false;
        const key = (source.dataset.switchFilterKey || '').trim().toLowerCase();
        const label = (source.dataset.switchFilterLabel || key).trim();
        const cards = source.querySelectorAll('[data-builder-item][data-component="switches"]');
        if (cards.length && key && !this.switchCategories.some((category) => category.key === key)) {
          this.switchCategories.push({ key, label });
        }
        cards.forEach((card) => {
          if (!card.dataset.switchType) card.dataset.switchType = label;
          if (key) card.dataset.feelKey = key;
        });
      });

      Array.from(this.querySelectorAll('[data-builder-item]')).forEach((card) => this.expandVariantCards(card));
      this.cards = Array.from(this.querySelectorAll('[data-builder-item]'));
      this.cards.forEach((card) => this.prepareCard(card));

      this.buildStepScaffolding();
      this.buildChrome();
      this.buildFeaturedBuilds();
      this.updateEmptyStates();

      this.nextButton?.addEventListener('click', () => this.handlePrimaryAction());
      this.backButton?.addEventListener('click', () => this.goToStep(this.currentStep - 1));
      this.querySelectorAll('[data-builder-share]').forEach((button) => button.addEventListener('click', () => this.copyBuildLink()));
      this.querySelector('[data-builder-drawer-close]')?.addEventListener('click', () => this.closeDrawer());
      this.querySelector('[data-builder-toast-close]')?.addEventListener('click', () => this.hideToast());
      this.querySelector('[data-builder-scrim]')?.addEventListener('click', () => this.closeDrawer());
      this.querySelector('[data-builder-mobile-progress]')?.addEventListener('click', () => this.openDrawer());
      document.addEventListener('shopify:block:select', this.handleThemeBlockSelect);
      document.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('resize', this.handleResize);

      this.hydrate();
      this.isHydrating = false;
      this.filterLayout();
      this.filterCompatibility({ silent: true });
      this.goToStep(this.currentStep, { force: true, silent: true });
      this.updateAll();
      this.adoptCart();
      this.restoreRemoteBuild();

      if (this.pendingSavedRestore) {
        const saved = this.pendingSavedRestore;
        this.pendingSavedRestore = null;
        let message = this.t('linkReplaced', 'This link opened a shared build. Your own saved build was set aside.');
        if (this.pendingDroppedNames?.length) {
          message = `${this.tpl('restoreDropped', { name: this.joinNames(this.pendingDroppedNames) }, '{name} couldn’t be restored from this link, so it’s not in the build.')} ${message}`;
          this.pendingDroppedNames = null;
        }
        this.showToast(message, () => this.restoreState(saved));
      }

      if (window.Shopify?.designMode) this.runDiagnostics();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.handleThemeBlockSelect);
      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('resize', this.handleResize);
    }

    handleResize() {
      this.syncMastheadHeight();
    }

    // the pinned masthead unit's height varies per instance (client chip,
    // wrapped headings) — mirror it into a CSS var so the sheet panel can
    // pin exactly below it. Measured AFTER updateTabs renders the rail
    // (pre-render reads once returned a stale 104px) and only written on
    // real change so idle updates never touch style.
    syncMastheadHeight() {
      const masthead = this.querySelector('[data-builder-masthead]');
      if (!masthead) return;
      const height = masthead.offsetHeight;
      if (height && height !== this.lastMastheadHeight) {
        this.lastMastheadHeight = height;
        this.style.setProperty('--kbv2-masthead-h', `${height}px`);
      }
    }

    /* ---------- strings ---------- */

    t(key, fallback = '') {
      const value = key.split('.').reduce((node, part) => (node && typeof node === 'object' ? node[part] : undefined), this.strings);
      return value == null || value === '' ? fallback : this.decodeEntities(String(value));
    }

    // Shopify hands `| t` an HTML-escaped string for any translation edited in
    // the theme-content editor (the live page carried "You\u0026#39;ve"), and
    // every consumer here escapes again on the way into the DOM — so the
    // customer saw a literal &#39;. Decode once, at the point strings enter
    // the builder; escapeHtml/textContent downstream keep it safe.
    decodeEntities(text) {
      if (!/&(#\d+|#x[0-9a-f]+|[a-z]+);/i.test(text)) return text;
      const area = document.createElement('textarea');
      area.innerHTML = text;
      return area.value;
    }

    tpl(key, replacements = {}, fallback = '') {
      let text = this.t(key, fallback);
      Object.entries(replacements).forEach(([token, value]) => {
        text = text.replaceAll(`{${token}}`, () => String(value));
      });
      return text;
    }

    joinNames(names) {
      // toast name lists read as a sentence: "A", "A and B", "A, B and C"
      const list = (names || []).filter(Boolean);
      if (list.length <= 1) return list[0] || '';
      const and = this.t('joinAnd', 'and');
      return `${list.slice(0, -1).join(', ')} ${and} ${list[list.length - 1]}`;
    }

    /* ---------- step discovery ---------- */

    getStepDefinitions() {
      const seen = new Set();
      const definitions = Array.from(this.querySelectorAll('[data-builder-step-block]'))
        .map((block) => ({
          block,
          key: (block.dataset.stepKey || '').trim().toLowerCase(),
          label: (block.dataset.stepLabel || block.dataset.stepKey || 'Step').trim(),
          required: block.dataset.stepRequired === 'true',
          selectionMode: block.dataset.stepSelectionMode || 'single',
          review: block.dataset.stepReview === 'true'
        }))
        .filter((definition) => {
          if (!definition.key || seen.has(definition.key)) return false;
          seen.add(definition.key);
          return true;
        });

      const layout = definitions.find((definition) => definition.key === 'layout');
      const keyboard = definitions.find((definition) => definition.key === 'keyboard');
      const review = definitions.find((definition) => definition.review || definition.key === 'review');
      const flexible = definitions.filter((definition) => !['layout', 'keyboard', 'review'].includes(definition.key) && !definition.review);
      return [layout, keyboard, ...flexible, review]
        .filter(Boolean)
        .map((definition, index) => {
          const assembly = definition.key === 'services' && definition.selectionMode === 'single';
          return {
            ...definition,
            index,
            assembly,
            // an either/or assembly step is a real decision, not an add-on —
            // it never wears the Optional tag
            optional: !definition.required && !definition.review && definition.key !== 'layout' && !assembly
          };
        });
    }

    definitionFor(key) {
      return this.stepDefinitions.find((definition) => definition.key === key);
    }

    choiceDefinitions() {
      return this.stepDefinitions.filter((definition) => !definition.review);
    }

    productDefinitions() {
      return this.stepDefinitions.filter((definition) => definition.key !== 'layout' && !definition.review);
    }

    requiredDefinitions() {
      return this.choiceDefinitions().filter((definition) => definition.required || definition.key === 'layout');
    }

    cardsFor(component) {
      return this.cards.filter((card) => card.dataset.component === component);
    }

    /* ---------- card preparation ---------- */

    prepareCard(card) {
      card.querySelector('[data-builder-select]')?.addEventListener('click', () => this.selectCard(card));
      card.querySelector('[data-builder-variant]')?.addEventListener('change', () => this.onVariantChange(card));

      // sold-out products are hidden outright — customers never see what we
      // don't have; a card with any purchasable variant stays, pointed at one
      card.dataset.stocked = String(this.cardHasStock(card));
      if (card.dataset.stocked === 'false') card.classList.add('is-soldout');

      const priceNode = card.querySelector('[data-builder-card-price]');
      if (priceNode) card.dataset.priceHidden = String(priceNode.textContent.trim() === '');
      // a real S$0 variant reads "Free", matching the sheet and review
      if (priceNode && card.dataset.priceHidden !== 'true' && Number(this.getVariant(card)?.price || 0) === 0) {
        priceNode.textContent = this.t('free', 'Free');
      }

      if (card.dataset.component === 'switches') {
        if (!card.dataset.feelKey) card.dataset.feelKey = this.feelOf(card);
        this.buildMeters(card);
      }
      if (card.dataset.component === this.assemblyDefinition?.key) {
        const image = card.querySelector('.ktechs-builder-card__image');
        // lazyload images carry the real URL in data-src; currentSrc at init
        // is only the 20px blur placeholder
        if (image) card.dataset.origImage = image.dataset.src || image.currentSrc || image.src || '';
      }
    }

    cardHasStock(card) {
      if (card.querySelector('[data-builder-select]')?.disabled) return false;
      const control = card.querySelector('[data-builder-variant]');
      if (!control) return false;
      if (control.tagName === 'SELECT') {
        return Array.from(control.options).some((option) => option.dataset.available === 'true' && !option.disabled);
      }
      return control.dataset.available === 'true';
    }

    expandVariantCards(card) {
      // one card per purchasable variant — customers pick a card, they never
      // toggle a dropdown; sold-out variants simply don't get a card
      const control = card.querySelector('[data-builder-variant]');
      if (control?.tagName !== 'SELECT') return;
      const options = Array.from(control.options).filter((option) => option.dataset.available === 'true' && !option.disabled);
      if (!options.length) return; // fully sold out — cardHasStock hides the card

      const host = card.closest('.ktechs-builder__theme-item') || card;
      const clones = options.slice(1).map(() => {
        const clone = host.cloneNode(true);
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
        return clone;
      });
      let anchor = host;
      clones.forEach((clone) => {
        anchor.after(clone);
        anchor = clone;
      });
      this.materializeVariantCard(card, options[0]);
      clones.forEach((clone, index) => {
        const target = clone.matches('[data-builder-item]') ? clone : clone.querySelector('[data-builder-item]');
        if (target) this.materializeVariantCard(target, options[index + 1]);
      });
    }

    materializeVariantCard(card, option) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('data-builder-variant', '');
      input.value = option.value;
      input.dataset.title = option.dataset.title || '';
      input.dataset.price = option.dataset.price || '0';
      input.dataset.priceFormatted = option.dataset.priceFormatted || '';
      input.dataset.available = option.dataset.available || 'false';
      if (option.dataset.image) input.dataset.image = option.dataset.image;
      if (option.dataset.description) input.dataset.description = option.dataset.description;
      if (option.dataset.layouts) input.dataset.layouts = option.dataset.layouts;
      const wrap = card.querySelector('[data-builder-variant-wrap]');
      if (wrap) wrap.replaceWith(input);
      else card.querySelector('[data-builder-variant]')?.replaceWith(input);
      card.dataset.variantId = option.value;

      const image = card.querySelector('.ktechs-builder-card__image');
      if (image && option.dataset.image) {
        image.src = option.dataset.image;
        image.srcset = option.dataset.image;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
      }
      const price = card.querySelector('[data-builder-card-price]');
      if (price && price.textContent.trim() !== '') {
        price.textContent = Number(option.dataset.price || 0) === 0
          ? this.t('free', 'Free')
          : (option.dataset.priceFormatted || this.formatMoney(Number(option.dataset.price || 0)));
      }
      const title = option.dataset.title || '';
      if (title && !title.toLowerCase().includes('default')) {
        const titleNode = card.querySelector('.ktechs-builder-card__title');
        if (card.closest('.ktechs-builder__cards--services')) {
          // service cards sell the offer, not the product: the variant name
          // IS the card title, and its builder_description metafield the copy
          if (titleNode) titleNode.textContent = title;
          if (option.dataset.description) {
            let description = card.querySelector('.ktechs-builder-card__description');
            if (!description && titleNode) {
              description = document.createElement('span');
              description.className = 'ktechs-builder-card__description';
              titleNode.after(description);
            }
            if (description) description.textContent = option.dataset.description;
          }
        } else if (titleNode) {
          const line = document.createElement('span');
          line.className = 'kbv2-variant-name';
          line.textContent = title;
          titleNode.after(line);
        }
      }
    }

    findCard(componentKey, handle, variantId) {
      const candidates = this.cardsFor(componentKey).filter((card) => card.dataset.productHandle === handle);
      if (!candidates.length) return null;
      if (variantId) {
        // the exact variant was saved/shared — never silently substitute a
        // different colorway or weight; callers treat null as a drop
        return candidates.find((card) => String(this.getVariant(card)?.id) === String(variantId)) || null;
      }
      return candidates.find((card) => card.dataset.stocked !== 'false') || candidates[0];
    }

    productTitleFor(componentKey, handle) {
      const card = this.cardsFor(componentKey).find((candidate) => candidate.dataset.productHandle === handle);
      return card?.dataset.productTitle || '';
    }

    feelOf(card) {
      const explicit = (card.dataset.feel || '').trim().toLowerCase();
      if (explicit) return explicit;
      const type = (card.dataset.switchType || '').toLowerCase();
      if (type.includes('tactile')) return 'bump';
      if (type.includes('click')) return 'clicky';
      return 'smooth';
    }

    buildMeters(card) {
      const sound = Number(card.dataset.soundLevel);
      const bump = Number(card.dataset.feelLevel);
      if (!Number.isFinite(sound) && !Number.isFinite(bump)) return;
      const content = card.querySelector('.ktechs-builder-card__content');
      const footer = card.querySelector('.ktechs-builder-card__footer');
      if (!content || !footer) return;

      const meters = document.createElement('span');
      meters.className = 'kbv2-meters';
      [[this.t('meterSound', 'Sound'), sound], [this.t('meterFeel', 'Bump'), bump]].forEach(([label, level]) => {
        if (!Number.isFinite(level)) return;
        const clamped = Math.max(0, Math.min(5, Math.round(level)));
        const meter = document.createElement('span');
        meter.className = 'kbv2-meter';
        meter.setAttribute('role', 'img');
        meter.setAttribute('aria-label', this.tpl('meterOf', { label, n: clamped }, `${label} ${clamped} of 5`));
        meter.innerHTML = `<small aria-hidden="true">${this.escapeHtml(label)}</small><span class="kbv2-meter__cells" aria-hidden="true">${[1, 2, 3, 4, 5].map((cell) => `<i${cell <= clamped ? ' class="on"' : ''}></i>`).join('')}</span>`;
        meters.appendChild(meter);
      });
      content.insertBefore(meters, footer);
    }

    /* ---------- static chrome ---------- */

    text(selector, value) {
      const node = this.querySelector(selector);
      // identical rewrites still mutate the DOM and make some screen readers
      // re-announce aria-live totals on every click — write only real changes
      if (node && node.textContent !== value) node.textContent = value;
    }

    buildChrome() {
      this.text('[data-builder-top-eyebrow]', this.t('eyebrow'));
      this.text('[data-builder-top-heading]', this.t('heading'));
      this.querySelectorAll('[data-builder-share]').forEach((button) => {
        if (button.textContent !== this.t('share', 'Share')) button.textContent = this.t('share', 'Share');
      });
      this.text('[data-builder-strip-label]', this.t('strip.label', 'Start from one of ours'));
      this.text('[data-builder-strip-note]', this.t('strip.note', ''));
      this.text('[data-builder-panel-title]', this.t('panelTitle', 'Build sheet'));
      const drawerClose = this.querySelector('[data-builder-drawer-close]');
      if (drawerClose) {
        drawerClose.textContent = '✕';
        drawerClose.setAttribute('aria-label', this.t('mobileClose', 'Close'));
      }
      const toastClose = this.querySelector('[data-builder-toast-close]');
      if (toastClose) {
        toastClose.textContent = '✕';
        toastClose.setAttribute('aria-label', this.t('toastClose', 'Dismiss message'));
      }
      this.text('[data-builder-autosave-note]', this.t('autosave'));
      this.text('[data-builder-mobile-total-label]', this.isPackage() ? this.t('pkg.youPayLabel', 'You pay') : this.t('totalLabel', 'Your build'));
      this.text('[data-builder-back]', this.t('back', 'Back'));
    }

    buildStepScaffolding() {
      this.stepDefinitions.forEach((definition, index) => {
        definition.block.querySelectorAll('[data-builder-step-count]').forEach((node) => {
          node.textContent = `${String(index + 1).padStart(2, '0')} / ${definition.label}`;
        });

        const panel = definition.block.querySelector('.ktechs-builder__panel');
        const heading = definition.block.querySelector('.ktechs-builder__panel-heading');
        const learn = this.strings.learn?.[definition.key];
        if (panel && heading && learn && learn.q && learn.a) {
          const details = document.createElement('details');
          details.className = 'kbv2-learn';
          details.innerHTML = `<summary>${this.escapeHtml(learn.q)}</summary><div class="kbv2-learn__body">${this.escapeHtml(learn.a)}</div>`;
          heading.insertAdjacentElement('afterend', details);
        }

        if (definition.key === 'layout' || definition.review) return;
        const cardsContainer = definition.block.querySelector('.ktechs-builder__cards');
        if (!cardsContainer) return;

        if (definition.key === 'switches') {
          const chipHost = definition.block.querySelector('[data-builder-switch-filters]');
          if (chipHost) this.buildFeelChips(chipHost);
          // the quantity NOTICE sits above the grid — seen before browsing,
          // styled as the same notice device the rest of the store uses
          cardsContainer.insertAdjacentHTML('beforebegin', '<div class="kbv2-qty-note" data-builder-qty-note hidden></div>');
        }

        const footer = document.createElement('div');
        footer.className = 'kbv2-step-footer';
        footer.innerHTML = `
          <div class="kbv2-grid-empty" data-builder-grid-empty hidden></div>
          <div class="kbv2-load-more" data-builder-load-more hidden>
            <button type="button" class="button outline small" data-fkey="loadmore:${definition.key}"></button>
            <small></small>
          </div>
          <div class="kbv2-hid-note" data-builder-hidden-note hidden>
            <span></span>
            <button type="button" data-fkey="hidshow:${definition.key}"></button>
          </div>`;
        cardsContainer.insertAdjacentElement('afterend', footer);
        footer.querySelector('[data-builder-load-more] button').addEventListener('click', () => {
          this.focusKey = `loadmore:${definition.key}`;
          this.shown[definition.key] += this.pageSize;
          this.updateAll();
        });
        footer.querySelector('[data-builder-hidden-note] button').addEventListener('click', () => {
          this.focusKey = `hidshow:${definition.key}`;
          this.reveal[definition.key] = !this.reveal[definition.key];
          this.updateAll();
        });
      });
    }

    buildFeelChips(host) {
      // staff-defined switch categories ARE the chips (Linear / Tactile /
      // Silent Linear / …); derived feel keys only cover uncategorized cards
      const categories = this.switchCategories || [];
      const categoryKeys = new Set(categories.map((category) => category.key));
      const derived = new Set(this.cardsFor('switches')
        .map((card) => card.dataset.feelKey)
        .filter((key) => key && !categoryKeys.has(key)));
      const order = ['smooth', 'bump', 'clicky'];
      const derivedKeys = [...order.filter((key) => derived.has(key)), ...Array.from(derived).filter((key) => !order.includes(key)).sort()];

      this.feelLabels = {};
      categories.forEach((category) => { this.feelLabels[category.key] = category.label; });
      derivedKeys.forEach((key) => { this.feelLabels[key] = this.t(`feels.${key}`, key.charAt(0).toUpperCase() + key.slice(1)); });

      // no "All" chip — the first real category is the default lens
      const keys = [...categories.map((category) => category.key), ...derivedKeys];
      if (keys.length < 2) {
        host.hidden = true;
        return;
      }
      if (!keys.includes(this.swFeel)) this.swFeel = keys[0];
      host.innerHTML = '';
      host.className = 'kbv2-chips';
      host.hidden = false;
      keys.forEach((key) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.builderFeelChip = key;
        button.dataset.fkey = `chip:${key}`;
        button.setAttribute('aria-pressed', String(this.swFeel === key));
        button.textContent = this.feelLabels[key];
        button.addEventListener('click', () => {
          this.focusKey = `chip:${key}`;
          this.swFeel = key;
          this.shown.switches = this.pageSize;
          this.updateAll();
        });
        host.appendChild(button);
      });
    }

    updateEmptyStates() {
      this.stepDefinitions.forEach((definition) => {
        const empty = definition.block.querySelector('[data-builder-empty-options]');
        if (!empty) return;
        const count = definition.key === 'layout'
          ? definition.block.querySelectorAll('[data-builder-layout]').length
          : definition.block.querySelectorAll('[data-builder-item]').length;
        empty.hidden = count > 0;
      });
    }

    /* ---------- featured builds ---------- */

    buildFeaturedBuilds() {
      const strip = this.querySelector('[data-builder-strip]');
      const grid = this.querySelector('[data-builder-featured-grid]');
      if (!grid) return;
      grid.innerHTML = '';
      let rendered = 0;

      this.querySelectorAll('[data-builder-featured-build]').forEach((source) => {
        const resolved = this.resolveFeaturedBuild(this.featuredQuery(source));
        if (!resolved) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'kbv2-fbmini';
        const image = resolved.image;
        const title = source.dataset.title?.trim()
          || resolved.picks.find((pick) => pick.definition.key === 'keyboard')?.card.dataset.productTitle
          || '';
        button.innerHTML = `
          <span class="kbv2-fbmini__img">${image ? `<img src="${this.escapeAttribute(image)}" alt="" loading="lazy" width="64" height="64">` : ''}</span>
          <span class="kbv2-fbmini__body">
            <strong>${this.escapeHtml(title)}</strong>
            <small>${this.escapeHtml(source.dataset.subtitle || '')}</small>
            <span class="kbv2-fbmini__price">${this.escapeHtml(this.t('strip.from', 'From'))} ${this.formatMoney(resolved.total)}</span>
          </span>
          <span class="kbv2-fbmini__go" aria-hidden="true">›</span>`;
        button.addEventListener('click', () => this.applyFeaturedBuild(resolved));
        grid.appendChild(button);
        rendered += 1;
      });

      this.stripRendered = rendered;
      this.updateStrip();
    }

    updateStrip() {
      const strip = this.querySelector('[data-builder-strip]');
      if (!strip) return;
      // the strip is an entry shortcut — it earns its place only while the
      // build is still empty (the editor keeps it visible for staff)
      const hasSelection = Boolean(this.layout)
        || this.productDefinitions().some((definition) => this.selectedCards(definition.key).length > 0);
      const shouldHide = !this.stripRendered || (hasSelection && !window.Shopify?.designMode);

      if (!shouldHide) {
        clearTimeout(this.stripLeaveTimer);
        strip.classList.remove('is-leaving');
        strip.style.removeProperty('height');
        strip.hidden = false;
        return;
      }
      if (strip.hidden || strip.classList.contains('is-leaving')) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        strip.hidden = true;
        return;
      }
      // collapse gracefully instead of snapping the page up: pin the current
      // height, transition it (with padding/opacity) to zero, then hide
      strip.style.height = `${strip.scrollHeight}px`;
      strip.getBoundingClientRect();
      strip.classList.add('is-leaving');
      strip.style.height = '0px';
      this.stripLeaveTimer = setTimeout(() => {
        strip.hidden = true;
        strip.classList.remove('is-leaving');
        strip.style.removeProperty('height');
      }, 400);
    }

    /* ---------- theme-editor diagnostics (design mode only) ---------- */

    runDiagnostics() {
      const box = this.querySelector('[data-builder-diag]');
      if (!box) return;
      const issues = [];
      const uniqueTitles = (cards) => [...new Set(cards.map((card) => card.dataset.productTitle || card.dataset.productHandle))];
      // long product lists stay readable: first few names + a count
      const nameList = (titles) => (titles.length > 6
        ? `${titles.slice(0, 6).join(', ')} + ${titles.length - 6} more`
        : titles.join(', '));

      // collections that are unset, empty, or larger than their limit (Liquid markers)
      this.querySelectorAll('[data-kbv2-diag-collection]').forEach((marker) => {
        if (marker.dataset.diagState === 'truncated') {
          const excludedSlots = Number(marker.dataset.diagExcluded || 0);
          const excludedNote = excludedSlots > 0 ? ` (${excludedSlots} of those slots went to tag-hidden products)` : '';
          issues.push(`${marker.dataset.diagWhere}: collection “${marker.dataset.diagCollectionTitle}” has ${marker.dataset.diagCount} products but only ${marker.dataset.diagLimit} can show${excludedNote} — raise “Maximum products” or split the collection.`);
          return;
        }
        if (marker.dataset.diagScope === 'step') {
          const step = marker.closest('[data-builder-step-block]');
          if (step && step.querySelector('[data-builder-item]')) return; // item blocks cover this step
        }
        if (marker.dataset.diagState === 'excluded-all') {
          issues.push(`${marker.dataset.diagWhere}: every product the grid can pull from collection “${marker.dataset.diagCollectionTitle}” is hidden by the “Hide products tagged” setting (“${marker.dataset.diagExcludeTag}”) — nothing renders from it.`);
          return;
        }
        issues.push(marker.dataset.diagState === 'unset'
          ? `${marker.dataset.diagWhere}: no collection assigned.`
          : `${marker.dataset.diagWhere}: collection “${marker.dataset.diagCollectionTitle}” has no products.`);
      });

      // exclude-tag entries that match no tag in their collection hide
      // nothing — name them and suggest the closest real tags to copy
      this.querySelectorAll('[data-kbv2-diag-tagmiss]').forEach((marker) => {
        const knownTags = (marker.dataset.diagKnownTags || '').split(',').filter(Boolean);
        (marker.dataset.diagMisses || '').split(',').filter(Boolean).forEach((miss) => {
          const near = knownTags.filter((tag) => {
            const known = tag.toLowerCase();
            if (known.includes(miss) || miss.includes(known) || known.slice(0, 4) === miss.slice(0, 4)) return true;
            const knownWords = known.split(/\s+/);
            return miss.split(/\s+/).some((word) => word.length > 2 && knownWords.includes(word));
          }).slice(0, 3);
          const hint = near.length ? ` Did you mean: ${near.map((tag) => `“${tag}”`).join(', ')}?` : '';
          issues.push(`${marker.dataset.diagWhere}: “Hide products tagged” entry “${miss}” matches no tag in collection “${marker.dataset.diagCollectionTitle}” — it hides nothing.${hint}`);
        });
      });

      // a services step set to multiple is no longer the either/or assembly
      // step: free preselect, review gating and the photo swap all turn off
      const servicesDefinition = this.definitionFor('services');
      if (servicesDefinition && servicesDefinition.selectionMode === 'multiple') {
        issues.push('Services step allows multiple selections — the assembly either/or behavior (free preselect, keyboard photo on the card) is OFF. Untick “Allow multiple selections” on the step.');
      }

      // a layout option nothing feeds renders an empty Keyboard step
      if (this.definitionFor('keyboard')) {
        Object.entries(this.layoutLabels).forEach(([key, label]) => {
          if (!this.cardsFor('keyboard').some((card) => card.dataset.layoutKey === key)) {
            issues.push(`Layout “${label}” has no keyboards — assign a keyboard collection on its Layout option.`);
          }
        });
      }

      // two Layout options sharing one key: the duplicate feeds the first's
      // grid and its own button never reads selected
      const layoutKeyCounts = {};
      this.layoutButtons.forEach((button) => {
        const key = button.dataset.builderLayout?.trim();
        if (key) layoutKeyCounts[key] = (layoutKeyCounts[key] || 0) + 1;
      });
      Object.entries(layoutKeyCounts).forEach(([key, count]) => {
        if (count > 1) issues.push(`${count} Layout options share the layout key “${key}” — give each option its own key.`);
      });

      // a product hand-picked as an item block AND present in the step's
      // collection renders twice — two independently selectable cards
      this.productDefinitions().forEach((definition) => {
        const inBlocks = new Set(
          this.cardsFor(definition.key)
            .filter((card) => card.closest('.ktechs-builder__theme-item'))
            .map((card) => card.dataset.productHandle)
        );
        const dupes = [...new Set(
          this.cardsFor(definition.key)
            .filter((card) => !card.closest('.ktechs-builder__theme-item') && inBlocks.has(card.dataset.productHandle))
            .map((card) => card.dataset.productTitle || card.dataset.productHandle)
        )];
        if (dupes.length) {
          issues.push(`${definition.label}: ${nameList(dupes)} appear${dupes.length === 1 ? 's' : ''} both as an item block and in the step's collection — customers see duplicate cards.`);
        }
      });

      // hand-picked item blocks that render nothing for customers
      this.querySelectorAll('.ktechs-builder__theme-item').forEach((wrap) => {
        const card = wrap.querySelector('[data-builder-item]');
        const stepName = wrap.closest('[data-builder-step-block]')?.dataset.stepLabel || 'a step';
        if (!card) {
          issues.push(`An item block on ${stepName} has no product chosen — it renders nothing.`);
        } else if (card.dataset.stocked === 'false') {
          issues.push(`“${card.dataset.productTitle}” (item block on ${stepName}) is sold out — sold-out products are hidden from customers.`);
        }
      });

      // featured builds that will not render
      this.querySelectorAll('[data-builder-featured-build]').forEach((source) => {
        const reasons = [];
        if (!this.resolveFeaturedBuild(this.featuredQuery(source), reasons)) {
          issues.push(`Featured build “${source.dataset.title || 'Untitled'}” will not show: ${reasons[0] || 'the link could not be resolved'}.`);
        }
        // a typed variant name that matched nothing: the item fell back to the first in stock
        source.querySelectorAll('[data-preset-item][data-variant-miss]').forEach((item) => {
          if (item.dataset.variantMiss) issues.push(`Featured build “${source.dataset.title || 'Untitled'}”: no variant called “${item.dataset.variantMiss}” on ${item.dataset.productTitle || item.dataset.handle} — using the first in stock.`);
        });
        if (source.querySelector('[data-preset-item][data-empty]')) issues.push(`Featured build “${source.dataset.title || 'Untitled'}” has a Preset item with no product chosen.`);
      });

      // package-mode configuration traps (settings can't validate themselves)
      if (this.isPackage()) {
        if (!this.b2b.client) {
          issues.push('Package mode is on but “Client label” is empty — the client chip and the covered line read wrong. Set it in the section settings.');
        }
        if (!this.b2b.subsidy) {
          issues.push('Package mode with no subsidy — the covered line shows −S$0.00. Set “Package subsidy (S$)” or switch Mode back to Retail.');
        }
        if (this.b2b.cap > 0 && this.b2b.subsidy > this.b2b.cap) {
          issues.push('“Package subsidy” is larger than “Budget cap” — customers can never use the full subsidy.');
        }
        if (this.b2b.cap > 0) {
          this.querySelectorAll('[data-builder-featured-build]').forEach((source) => {
            const resolved = this.resolveFeaturedBuild(this.featuredQuery(source));
            if (resolved && resolved.total > this.b2b.cap) {
              issues.push(`Featured build “${source.dataset.title || 'Untitled'}” costs ${this.formatMoney(resolved.total)} — over the ${this.formatMoney(this.b2b.cap)} package budget${this.b2b.capBehavior === 'block' ? ', so customers who apply it are blocked at Review' : ''}.`);
            }
          });
        }
      }

      // no free service variant on the assembly step
      if (this.assemblyDefinition) {
        const hasFree = this.cardsFor(this.assemblyDefinition.key).some(
          (card) => card.dataset.stocked !== 'false' && Number(this.getVariant(card)?.price || 0) === 0
        );
        if (!hasFree) {
          issues.push('Assembly step has no in-stock free (S$0) service variant — nothing is preselected and “kit only” cannot be chosen.');
        }
        // service card copy hangs entirely on the variant metafield
        const missingDescription = [...new Set(
          this.cardsFor(this.assemblyDefinition.key)
            .filter((card) => !card.querySelector('.ktechs-builder-card__description'))
            .map((card) => card.querySelector('.ktechs-builder-card__title')?.textContent?.trim())
            .filter(Boolean)
        )];
        if (missingDescription.length) {
          issues.push(`Service variants without custom.builder_description (their cards show no description): ${nameList(missingDescription)}.`);
        }
      }

      // switch products missing pricing/type metafields (attrs from the card snippet)
      const noPack = uniqueTitles(this.cards.filter((card) => card.hasAttribute('data-diag-no-pack')));
      if (noPack.length) {
        issues.push(`Switches without custom.pack_quantity (a pack price would be multiplied per key): ${nameList(noPack)}.`);
      }
      const noType = uniqueTitles(this.cards.filter((card) => card.hasAttribute('data-diag-no-switch-type')));
      if (noType.length) {
        issues.push(`Switches without custom.switch_type (card shows “Other” outside categories): ${nameList(noType)}.`);
      }
      const noMeters = uniqueTitles(this.cards.filter((card) => card.hasAttribute('data-diag-no-meters')));
      if (noMeters.length) {
        issues.push(`Switches without sound/feel meter metafields (custom.builder_sound_level / builder_feel_level — their meters won't show): ${nameList(noMeters)}.`);
      }

      const list = box.querySelector('[data-builder-diag-list]');
      const count = box.querySelector('[data-builder-diag-count]');
      if (!list || !count) return;
      list.innerHTML = '';
      const lines = issues.length ? issues : ['No issues found — collections, featured builds, switch metafields and the assembly step all check out.'];
      lines.forEach((text) => {
        const item = document.createElement('li');
        item.textContent = text;
        list.appendChild(item);
      });
      count.textContent = issues.length === 1 ? '1 thing to review' : issues.length ? `${issues.length} things to review` : 'All clear';
      box.classList.toggle('kbv2-diag--issues', issues.length > 0);
      box.open = issues.length > 0;
      box.hidden = false;
    }

    // Preset items (product + variant picked in the editor) beat a pasted link.
    featuredQuery(source) {
      const items = Array.from(source.querySelectorAll('[data-preset-item]'))
        .filter((item) => item.dataset.handle)
        .map((item) => `${item.dataset.handle}:${item.dataset.variantId || ''}:${item.dataset.option || ''}`);
      if (items.length) return `builder_items=${encodeURIComponent(items.join(','))}`;
      return source.dataset.query || '';
    }

    resolveFeaturedBuild(query, reasons = null) {
      const fail = (reason) => {
        if (reasons) reasons.push(reason);
        return null;
      };
      // staff may paste the whole Share URL, "?builder_…", or the bare query
      let raw = String(query || '').trim();
      const questionMark = raw.indexOf('?');
      if (questionMark !== -1) raw = raw.slice(questionMark + 1);
      const hashMark = raw.indexOf('#');
      if (hashMark !== -1) raw = raw.slice(0, hashMark);
      if (!raw) return fail('no Share link pasted yet');
      let params;
      try {
        params = new URLSearchParams(raw);
      } catch (_error) {
        return fail('the pasted link could not be read');
      }
      const items = (params.get('builder_items') || '').split(',').filter(Boolean);
      // products the customer decides the variant of (colour, weight — whatever
      // the product's option is called): pinned nowhere, several variants
      const chooseHandles = new Map();
      for (const pair of items) {
        // sort each picked product into the step whose cards carry it; the
        // keyboard's card names the layout. Then the same checks as a link.
        const [handle, variantId, ...optionParts] = pair.split(':');
        const optionName = optionParts.join(':');
        let placed = false;
        for (const definition of this.productDefinitions()) {
          const card = this.findCard(definition.key, handle, variantId || undefined);
          if (!card) continue;
          const key = definition.key;
          const handles = params.get(`builder_${key}`) || '';
          const variants = params.get(`builder_${key}_variant`) || '';
          const vid = String(this.getVariant(card)?.id || variantId || '');
          if (!variantId && this.cardsFor(key).filter((candidate) => candidate.dataset.productHandle === handle).length > 1) chooseHandles.set(handle, optionName);
          params.set(`builder_${key}`, handles ? `${handles},${handle}` : handle);
          params.set(`builder_${key}_variant`, variants ? `${variants},${vid}` : vid);
          if (key === 'keyboard' && card.dataset.layoutKey) params.set('builder_layout', card.dataset.layoutKey);
          placed = true;
          break;
        }
        if (!placed) {
          const anyVariant = this.productDefinitions().some((definition) => this.findCard(definition.key, handle));
          return fail(anyVariant
            ? `the chosen variant of "${handle}" is sold out or no longer exists`
            : `"${handle}" is not in any builder step (check it is in a builder collection and not hidden by tag)`);
        }
      }
      const layout = params.get('builder_layout');
      const hasLayoutStep = Boolean(this.definitionFor('layout'));
      if (hasLayoutStep && (!layout || !this.layoutLabels[layout])) return fail(items.length ? 'its keyboard has no layout key (is it in a layout collection?)' : 'its layout key does not match any Layout option');

      const picks = [];
      let keyboard = null;
      let total = 0;
      for (const definition of this.productDefinitions()) {
        const handles = (params.get(`builder_${definition.key}`) || '').split(',').filter(Boolean);
        const variants = (params.get(`builder_${definition.key}_variant`) || '').split(',').filter(Boolean);
        for (let index = 0; index < handles.length; index += 1) {
          const card = this.findCard(definition.key, handles[index], variants[index]);
          if (!card || card.dataset.stocked === 'false') return fail(`"${handles[index]}" is missing from the ${definition.key} step or sold out`);
          if (definition.key === 'keyboard' && hasLayoutStep && card.dataset.layoutKey !== layout) return fail(`keyboard "${handles[index]}" does not belong to layout "${layout}"`);
          const variant = this.getVariant(card);
          if (!variant || !variant.available) return fail(`a chosen variant of "${handles[index]}" is unavailable`);
          picks.push({ definition, card, variant, choose: chooseHandles.has(handles[index]), option: chooseHandles.get(handles[index]) || '' });
          if (definition.key === 'keyboard') keyboard = card;
        }
      }
      if (!picks.length) return fail('the link contains no builder products');
      if (this.definitionFor('keyboard') && !keyboard) return fail('the link has no keyboard');
      // a curated build must satisfy every required step — a partial one would
      // force-jump the customer past a step they can never unlock
      for (const definition of this.productDefinitions()) {
        if (definition.required && !picks.some((pick) => pick.definition.key === definition.key)) {
          return fail(`the link has no ${definition.label || definition.key} pick`);
        }
      }

      for (const pick of picks) {
        if (pick.definition.key === 'keyboard') continue;
        const key = (keyboard?.dataset.compatibilityKey || 'universal').toLowerCase();
        if (keyboard && !this.isCompatible(pick.card, key)) return fail(`"${pick.card.dataset.productTitle}" is not compatible with the keyboard`);
      }

      const required = Number(keyboard?.dataset.requiredSwitches || 0);
      picks.forEach((pick) => {
        const quantity = pick.definition.key === 'switches'
          ? this.packsFor(Math.max(1, Number(pick.card.dataset.packSize || 1)), required)
          : 1;
        total += pick.variant.price * quantity;
      });

      const image = keyboard ? this.cardImage(keyboard) : this.cardImage(picks[0].card);
      return { layout, picks, total, image };
    }

    applyFeaturedBuild(resolved) {
      this.clearAll();
      if (resolved.layout) this.selectLayout(resolved.layout, { silent: true });
      // a pick the customer must decide the variant of is not selected: that
      // step shows ONLY the product's variants under one instruction (the
      // preset lens), and the flow waits there. Merchant, 6 Sep: an outline
      // read as two things selected; showing only the choices does not.
      const choices = resolved.picks.filter((pick) => pick.choose);
      resolved.picks.forEach((pick) => {
        if (!pick.choose) this.selectCard(pick.card, { silent: true });
      });
      this.presetLens = {};
      choices.forEach((pick) => {
        this.presetLens[pick.definition.key] = {
          handle: pick.card.dataset.productHandle || '',
          name: pick.card.dataset.productTitle || '',
          option: pick.option || ''
        };
      });
      // never land past what the selections actually unlock, and hand focus
      // to the destination — the strip that held it hides itself now
      const firstChoice = choices.length ? Math.min(...choices.map((pick) => pick.definition.index)) : Infinity;
      const target = Math.min(
        firstChoice,
        this.assemblyDefinition ? this.assemblyDefinition.index : this.stepDefinitions.length - 1,
        this.highestUnlockedStep()
      );
      this.focusKey = `tab:${this.stepDefinitions[target]?.key}`;
      this.startBuilder(target);
    }

    // The preset lens: while a step waits for the customer's variant pick, it
    // shows only that product's cards and one line naming the decision in the
    // product's own terms ("Choose a weight for Y2"). Any pick on the step,
    // or "Show all", lifts it.
    updateLens() {
      this.productDefinitions().forEach((definition) => {
        const grid = definition.block?.querySelector('.ktechs-builder__cards');
        if (!grid) return;
        let note = definition.block.querySelector('[data-builder-lens]');
        const lens = this.presetLens?.[definition.key];
        if (!lens) {
          if (note) note.hidden = true;
          return;
        }
        if (!note) {
          note = document.createElement('div');
          note.className = 'kbv2-lens';
          note.setAttribute('data-builder-lens', '');
          grid.insertAdjacentElement('beforebegin', note);
        }
        const option = String(lens.option || '').trim().toLowerCase();
        // once the lens card is picked the prompt has been answered: only the
        // way out ("Show all") stays
        const picked = this.selectedCards(definition.key).some((card) => card.dataset.productHandle === lens.handle);
        const text = picked ? '' : (option
          ? this.tpl('presetChoose', { name: lens.name, option }, 'Choose a {option} for {name}.')
          : this.tpl('presetChooseGeneric', { name: lens.name }, 'Choose an option for {name}.'));
        const stepLabel = String(definition.label || definition.key).toLowerCase();
        note.classList.toggle('kbv2-lens--picked', picked);
        note.innerHTML = `${text ? `<p>${this.escapeHtml(text)}</p>` : ''}<button type="button" class="text-button" data-builder-lens-clear>${this.escapeHtml(this.tpl('presetShowAll', { step: stepLabel }, 'Show all {step}'))}</button>`;
        note.querySelector('[data-builder-lens-clear]').addEventListener('click', () => {
          delete this.presetLens[definition.key];
          this.updateAll();
        });
        note.hidden = false;
      });
    }

    startBuilder(step) {
      this.started = true;
      this.goToStep(step, { force: true, silent: true });
      this.updateAll();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }

    /* ---------- selection ---------- */

    selectLayout(layout, options = {}) {
      if (!layout || !this.layoutLabels[layout]) return;
      const changed = this.layout !== layout;
      if (!changed) {
        this.applyLayoutButtons();
        return;
      }

      const keyboard = this.firstSelected('keyboard');
      const keyboardRemoved = Boolean(keyboard && keyboard.dataset.layoutKey !== layout);
      const removals = [];
      if (keyboardRemoved) {
        removals.push(keyboard);
        const newKeyboards = this.cardsFor('keyboard').filter((card) => card.dataset.layoutKey === layout && card.dataset.stocked !== 'false');
        // no stocked keyboards in the new layout: compatibility is unknowable,
        // so keep everything else — filterCompatibility prunes later once a
        // keyboard exists (an empty list must not wipe universal parts)
        if (newKeyboards.length) {
          this.productDefinitions().filter((definition) => definition.key !== 'keyboard').forEach((definition) => {
            this.selectedCards(definition.key).forEach((card) => {
              const fitsSomewhere = newKeyboards.some((candidate) => this.isCompatible(card, (candidate.dataset.compatibilityKey || 'universal').toLowerCase()));
              if (!fitsSomewhere) removals.push(card);
            });
          });
        }
      }

      const snapshot = removals.length && !options.silent ? this.snapshotState() : null;
      this.layout = layout;
      removals.forEach((card) => this.deselectCard(card));
      this.applyLayoutButtons();
      this.filterLayout();
      this.filterCompatibility({ silent: true });

      if (snapshot) {
        const names = this.joinNames(removals.map((card) => card.dataset.productTitle));
        const message = removals.length === 1
          ? this.tpl('undoLayoutOne', { items: names }, `${names} removed.`)
          : this.tpl('undoLayoutMany', { items: names }, `${names} removed.`);
        this.showToast(message, () => this.restoreState(snapshot));
      }
      if (!options.silent) this.updateAll();
    }

    applyLayoutButtons() {
      this.layoutButtons.forEach((button) => {
        const selected = button.dataset.builderLayout === this.layout;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    }

    selectCard(card, options = {}) {
      const component = card.dataset.component;
      // the preset lens survives a pick: the chosen card stays alone in its
      // step, and only "Show all" widens the list (merchant, 6 Sep)
      const definition = this.definitionFor(component);
      if (!definition || !Object.prototype.hasOwnProperty.call(this.selections, component)) return;
      if (card.classList.contains('is-revealed')) return;

      let snapshot = null;
      if (component === 'keyboard' && !options.silent) {
        const key = (card.dataset.compatibilityKey || 'universal').toLowerCase();
        const doomed = this.productDefinitions()
          .filter((other) => other.key !== 'keyboard')
          .flatMap((other) => this.selectedCards(other.key))
          .filter((selected) => !this.isCompatible(selected, key));
        if (doomed.length) snapshot = this.snapshotState();
      }

      if (definition.selectionMode === 'multiple') {
        const selected = this.selections[component];
        const index = selected.indexOf(card);
        if (index >= 0) {
          selected.splice(index, 1);
          this.setSelected(card, false);
        } else {
          selected.push(card);
          this.setSelected(card, true);
        }
      } else {
        const previous = this.selections[component];
        if (previous === card && definition.assembly) return;
        if (previous && previous !== card) this.setSelected(previous, false);
        this.selections[component] = card;
        this.setSelected(card, true);
      }

      if (component === 'keyboard') {
        this.conflictChoice = null;
        const removed = this.filterCompatibility({ silent: true });
        if (snapshot && removed.length) {
          const names = this.joinNames(removed.map((item) => item.dataset.productTitle));
          this.showToast(this.tpl('undoKb', { items: names }, `${names} removed.`), () => this.restoreState(snapshot));
        }
      }
      if (!options.silent) this.updateAll();
    }

    deselectCard(card) {
      const component = card.dataset.component;
      const definition = this.definitionFor(component);
      if (!definition) return;
      if (definition.selectionMode === 'multiple') {
        this.selections[component] = (this.selections[component] || []).filter((selected) => selected !== card);
      } else if (this.selections[component] === card) {
        this.selections[component] = null;
      }
      this.setSelected(card, false);
    }

    clearSelection(component) {
      this.selectedCards(component).forEach((card) => this.setSelected(card, false));
      const definition = this.definitionFor(component);
      this.selections[component] = definition?.selectionMode === 'multiple' ? [] : null;
    }

    clearAll() {
      this.presetLens = {};
      this.productDefinitions().forEach((definition) => this.clearSelection(definition.key));
      this.layout = null;
      this.applyLayoutButtons();
      this.filterLayout();
      this.filterCompatibility({ silent: true });
    }

    setSelected(card, selected) {
      card.classList.toggle('is-selected', selected);
      card.querySelector('[data-builder-select]')?.setAttribute('aria-pressed', String(selected));
      const variantWrap = card.querySelector('[data-builder-variant-wrap]');
      if (variantWrap) variantWrap.hidden = !selected;
    }

    selectedCards(component) {
      const selected = this.selections[component];
      if (Array.isArray(selected)) return selected;
      return selected ? [selected] : [];
    }

    firstSelected(component) {
      return this.selectedCards(component)[0] || null;
    }

    /* ---------- variants ---------- */

    getVariant(card) {
      if (!card) return null;
      const control = card.querySelector('[data-builder-variant]');
      if (!control) return null;
      const read = (source) => ({
        id: Number(source.value || 0),
        title: source.dataset.title || '',
        price: Number(source.dataset.price || 0),
        formattedPrice: source.dataset.priceFormatted || '',
        available: source.dataset.available === 'true',
        image: source.dataset.image || ''
      });
      if (control.tagName === 'SELECT') {
        const option = control.options[control.selectedIndex];
        return option ? read(option) : null;
      }
      return read(control);
    }

    onVariantChange(card) {
      const variant = this.getVariant(card);
      const price = card.querySelector('[data-builder-card-price]');
      if (price && variant && card.dataset.priceHidden !== 'true') {
        price.textContent = variant.price === 0 ? this.t('free', 'Free') : (variant.formattedPrice || this.formatMoney(variant.price));
      }
      const image = card.querySelector('.ktechs-builder-card__image');
      if (image && variant?.image) {
        image.src = variant.image;
        image.srcset = variant.image;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
      }
      // choosing a variant is the pick: the card selects itself so Continue
      // opens without a second tap (merchant, 6 Sep)
      if (!card.classList.contains('is-selected') && variant?.available !== false) {
        this.selectCard(card);
      }
      this.updateAll();
    }

    cardImage(card) {
      if (!card) return '';
      const variant = this.getVariant(card);
      if (variant?.image) return variant.image;
      const image = card.querySelector('.ktechs-builder-card__image');
      return image?.currentSrc || image?.src || '';
    }

    /* ---------- filtering ---------- */

    isCompatible(card, key) {
      const values = (card.dataset.compatibility || 'universal')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
      return values.includes('universal') || values.includes(key);
    }

    filterLayout() {
      const hasLayoutStep = Boolean(this.definitionFor('layout'));
      this.querySelectorAll('[data-builder-keyboard-source]').forEach((source) => {
        source.hidden = hasLayoutStep && (!this.layout || source.dataset.layoutKey !== this.layout);
      });
    }

    // Layout fit — the merchant's per-variant `custom.builder_layouts` metafield
    // (a list of layout keys: 65, 75, 80, full) says which boards a variant fits.
    // A carrying case comes in Small (65) and Medium (75, 80); a full-size build
    // fits neither, so the case must not appear at all. Fail-closed by product:
    // once ANY variant of a product carries layout data, a sibling variant with
    // none is treated as fitting nothing — an unlabelled colour variant can't
    // slip past the size rule. Products with no layout data anywhere are
    // universal and untouched (cloths, artisans). Merchant edit point: the
    // variant's metafields in admin; nothing here names a product or a size.
    filterFit() {
      const layout = (this.layout || '').toLowerCase();
      const removed = [];
      this.productDefinitions().filter((definition) => definition.key !== 'keyboard').forEach((definition) => {
        const cards = this.cardsFor(definition.key);
        const sized = new Set();
        cards.forEach((card) => {
          if ((card.querySelector('[data-builder-variant]')?.dataset.layouts || '').trim()) sized.add(card.dataset.productId);
        });
        cards.forEach((card) => {
          let fits = true;
          if (sized.has(card.dataset.productId)) {
            const list = (card.querySelector('[data-builder-variant]')?.dataset.layouts || '')
              .split(',').map((value) => value.trim().toLowerCase()).filter(Boolean);
            fits = Boolean(layout) && list.includes(layout);
          }
          card.dataset.fitVisible = String(fits);
          if (!fits && this.selectedCards(definition.key).includes(card)) {
            this.deselectCard(card);
            removed.push(card);
          }
        });
      });
      return removed;
    }

    filterCompatibility(options = {}) {
      const keyboard = this.firstSelected('keyboard');
      const key = (keyboard?.dataset.compatibilityKey || 'universal').toLowerCase();
      const removed = [];

      this.productDefinitions().filter((definition) => definition.key !== 'keyboard').forEach((definition) => {
        this.cardsFor(definition.key).forEach((card) => {
          const compatible = !keyboard || this.isCompatible(card, key);
          card.dataset.compatibilityVisible = String(compatible);
          if (!compatible && this.selectedCards(definition.key).includes(card)) {
            this.deselectCard(card);
            removed.push(card);
          }
        });
      });
      // sizes that no longer fit the chosen layout leave with the same undo toast
      removed.push(...this.filterFit());

      if (!options.silent) this.updateAll();
      return removed;
    }

    applyStepVisibility() {
      this.productDefinitions().forEach((definition) => {
        const keyboardStep = definition.key === 'keyboard';
        const cards = this.cardsFor(definition.key);
        const eligible = [];
        const incompatible = [];
        // pagination and the feel lens must never hide a card the customer
        // has selected (restored builds can point past the first page)
        const selectedSet = new Set(this.selectedCards(definition.key));

        cards.forEach((card) => {
          card.classList.remove('is-revealed');
          if (card.dataset.stocked === 'false') {
            this.setCardVisibility(card, false);
            return;
          }
          const lens = this.presetLens?.[definition.key];
          if (lens && card.dataset.productHandle !== lens.handle) {
            this.setCardVisibility(card, false);
            return;
          }
          if (keyboardStep) {
            const source = card.closest('[data-builder-keyboard-source]');
            const layoutOk = source ? !source.hidden : (!this.definitionFor('layout') || (Boolean(this.layout) && card.dataset.layoutKey === this.layout));
            if (layoutOk) eligible.push(card);
            else this.setCardVisibility(card, false);
            return;
          }
          if (card.dataset.fitVisible === 'false') {
            // wrong size for this layout: not listed, not even as "doesn't fit"
            this.setCardVisibility(card, false);
            return;
          }
          const compatible = card.dataset.compatibilityVisible !== 'false';
          // the customer's own selection stays visible under any feel lens —
          // comparing chips must never hide what they already chose
          const feelOk = definition.key !== 'switches' || this.swFeel === 'all' || card.dataset.feelKey === this.swFeel || selectedSet.has(card);
          if (compatible && feelOk) eligible.push(card);
          else if (!compatible && feelOk) incompatible.push(card);
          else this.setCardVisibility(card, false);
        });

        let shown = this.shown[definition.key] || this.pageSize;
        const lastSelectedIndex = eligible.reduce((max, card, index) => (selectedSet.has(card) ? index : max), -1);
        if (lastSelectedIndex >= shown) {
          shown = lastSelectedIndex + 1;
          this.shown[definition.key] = shown;
        }
        const visible = eligible.slice(0, shown);
        eligible.forEach((card, index) => this.setCardVisibility(card, index < shown));

        const revealed = this.reveal[definition.key];
        incompatible.forEach((card) => {
          this.setCardVisibility(card, revealed);
          if (revealed) {
            card.classList.add('is-revealed');
            const button = card.querySelector('[data-builder-select]');
            button?.setAttribute('aria-disabled', 'true');
          }
        });
        cards.forEach((card) => {
          if (!card.classList.contains('is-revealed')) card.querySelector('[data-builder-select]')?.removeAttribute('aria-disabled');
        });

        const footer = definition.block.querySelector('.kbv2-step-footer');
        if (!footer) return;

        const loadMore = footer.querySelector('[data-builder-load-more]');
        if (loadMore) {
          const remaining = eligible.length - visible.length;
          loadMore.hidden = remaining <= 0;
          if (remaining > 0) {
            loadMore.querySelector('button').textContent = this.tpl('loadMore', { n: Math.min(this.pageSize, remaining) }, 'Show more');
            loadMore.querySelector('small').textContent = this.tpl('loadShowing', { a: visible.length, b: eligible.length }, '');
          }
        }

        const note = footer.querySelector('[data-builder-hidden-note]');
        if (note) {
          const keyboardName = this.firstSelected('keyboard')?.dataset.productTitle || '';
          note.hidden = incompatible.length === 0;
          if (incompatible.length) {
            note.querySelector('span').textContent = incompatible.length === 1
              ? this.tpl('hiddenNoteOne', { keyboard: keyboardName }, '1 option hidden.')
              : this.tpl('hiddenNoteMany', { n: incompatible.length, keyboard: keyboardName }, `${incompatible.length} options hidden.`);
            note.querySelector('button').textContent = revealed ? this.t('hiddenHide', 'Hide again') : this.t('hiddenShow', 'Show anyway');
          }
        }

        const gridEmpty = footer.querySelector('[data-builder-grid-empty]');
        if (gridEmpty) {
          const empty = eligible.length === 0 && cards.length > 0
            && (definition.key === 'switches' || definition.key === 'keyboard');
          gridEmpty.hidden = !empty;
          if (empty) {
            gridEmpty.textContent = definition.key === 'keyboard'
              ? this.tpl('gridEmptyKeyboard', { layout: this.layoutLabels[this.layout] || '' }, '')
              : this.feelLabels?.[this.swFeel]
                ? this.tpl('gridEmpty', {
                    feel: this.feelLabels[this.swFeel].toLowerCase(),
                    keyboard: this.firstSelected('keyboard')?.dataset.productTitle || ''
                  }, '')
                : this.t('gridEmptyAll', 'No switches fit this build. Show the hidden switches below.');
          }
        }
      });

      this.querySelectorAll('[data-builder-feel-chip]').forEach((chip) => {
        const key = chip.dataset.builderFeelChip;
        const active = this.swFeel === key;
        chip.setAttribute('aria-pressed', String(active));
        chip.classList.toggle('is-active', active);
        if (key !== 'all') {
          const keyboard = this.firstSelected('keyboard');
          const compatKey = (keyboard?.dataset.compatibilityKey || 'universal').toLowerCase();
          const count = this.cardsFor('switches').filter((card) => card.dataset.feelKey === key && card.dataset.stocked !== 'false' && (!keyboard || this.isCompatible(card, compatKey))).length;
          chip.disabled = count === 0;
        }
      });

      // without an "All" chip the active lens must always be a live one —
      // if compatibility empties it, fall over to the first usable chip
      const activeChip = this.querySelector(`[data-builder-feel-chip="${this.swFeel}"]`);
      if (activeChip?.disabled && !this.feelRetryGuard) {
        const firstEnabled = Array.from(this.querySelectorAll('[data-builder-feel-chip]')).find((chip) => !chip.disabled);
        if (firstEnabled && firstEnabled.dataset.builderFeelChip !== this.swFeel) {
          this.feelRetryGuard = true;
          this.swFeel = firstEnabled.dataset.builderFeelChip;
          this.applyStepVisibility();
          this.feelRetryGuard = false;
        }
      }
    }

    setCardVisibility(card, visible) {
      card.hidden = !visible;
      const itemWrapper = card.closest('.ktechs-builder__theme-item');
      if (itemWrapper) itemWrapper.hidden = !visible;
    }

    /* ---------- quantities and totals ---------- */

    requiredSwitches() {
      return Number(this.firstSelected('keyboard')?.dataset.requiredSwitches || 0);
    }

    packsFor(packSize, keys) {
      if (!keys) return 1;
      const size = Math.max(1, packSize);
      const base = Math.max(1, Math.ceil(keys / size));
      return (base * size) - keys >= this.minSpares ? base : base + 1;
    }

    switchQuantity(card) {
      const switchCard = card || this.firstSelected('switches');
      if (!switchCard) return 0;
      return this.packsFor(Math.max(1, Number(switchCard.dataset.packSize || 1)), this.requiredSwitches());
    }

    entryQuantity(definition, card) {
      return definition.key === 'switches' ? this.switchQuantity(card) : 1;
    }

    selectedEntries() {
      return this.productDefinitions().flatMap((definition) => this.selectedCards(definition.key).flatMap((card) => {
        const variant = this.getVariant(card);
        if (!variant) return [];
        return [{
          component: definition.key,
          definition,
          label: definition.label,
          card,
          variant,
          quantity: this.entryQuantity(definition, card)
        }];
      }));
    }

    totalPrice() {
      return this.selectedEntries().reduce((total, entry) => total + (entry.variant.price * entry.quantity), 0);
    }

    /* ---------- package mode (B2B) ---------- */
    // math validated in the Builder Pro proposal: the client covers up to the
    // subsidy, the customer pays the rest; a cap either blocks at Review or
    // lets the customer top the difference up. Checkout enforcement is the
    // client's fixed-amount discount code — this is display, not pricing.

    isPackage() {
      return this.b2b?.mode === 'package';
    }

    coveredAmount(total) {
      return this.isPackage() ? Math.min(this.b2b.subsidy, total) : 0;
    }

    overCap(total) {
      return this.isPackage() && this.b2b.cap > 0 && total > this.b2b.cap;
    }

    capBlocked() {
      return this.overCap(this.totalPrice()) && this.b2b.capBehavior === 'block';
    }

    /* ---------- progress ---------- */

    stepSatisfied(definition) {
      if (!definition) return false;
      if (definition.key === 'layout') return Boolean(this.layout);
      if (definition.review) {
        return this.choiceDefinitions().every((choice) => this.stepSatisfied(choice));
      }
      if (definition.assembly) {
        if (this.selectedCards(definition.key).length > 0) return true;
        return !definition.required;
      }
      if (definition.required) return this.selectedCards(definition.key).length > 0;
      return true;
    }

    stepHasSelection(definition) {
      if (definition.key === 'layout') return Boolean(this.layout);
      if (definition.review) return this.stepSatisfied(definition);
      return this.selectedCards(definition.key).length > 0;
    }

    highestUnlockedStep() {
      let unlocked = 0;
      for (let index = 0; index < this.stepDefinitions.length - 1; index += 1) {
        if (!this.stepSatisfied(this.stepDefinitions[index])) break;
        unlocked = index + 1;
      }
      return unlocked;
    }

    completedRequiredCount() {
      return this.requiredDefinitions().filter((definition) => this.stepHasSelection(definition)).length;
    }

    allRequiredComplete() {
      return this.requiredDefinitions().every((definition) => this.stepHasSelection(definition));
    }

    goToStep(step, options = {}) {
      const target = Math.max(0, Math.min(this.stepDefinitions.length - 1, Number(step)));
      if (!options.force && target > this.highestUnlockedStep()) return;
      this.currentStep = target;

      const definition = this.stepDefinitions[target];
      // entering the assembly step — or Review, which the tab rail can reach
      // without ever visiting assembly — with nothing chosen: preselect the
      // free option (the S$0 kit-only variant) so continuing is never blocked,
      // the paid upsell is seen not forced, and every cart carries its
      // service line
      const assemblyTarget = definition?.assembly
        ? definition
        : (definition?.review ? this.assemblyDefinition : null);
      if (assemblyTarget && !this.selectedCards(assemblyTarget.key).length) {
        const freeCard = this.cardsFor(assemblyTarget.key).find((card) => card.dataset.stocked !== 'false' && Number(this.getVariant(card)?.price || 0) === 0);
        if (freeCard) this.selectCard(freeCard, { silent: true });
      }

      // entering the switches step with a selection made elsewhere (resume,
      // share link, featured build): align the feel lens to the selection so
      // the chosen card is never hidden by the default chip
      if (definition?.key === 'switches') {
        const selectedSwitch = this.firstSelected('switches');
        const feel = selectedSwitch?.dataset.feelKey;
        if (feel && this.swFeel !== 'all' && this.swFeel !== feel) this.swFeel = feel;
      }

      this.stepDefinitions.forEach((candidate, index) => {
        const active = index === target;
        candidate.block.hidden = !active;
        candidate.block.classList.toggle('is-active', active);
      });

      this.closeDrawer();
      if (!options.silent) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Snap to the top on EVERY step change, at every width — merchant
        // ruling, 2 Sep. The mobile snap-to-top is a feature customers like
        // (nothing says "you're on a new step" more clearly), and desktop and
        // tablet felt broken for NOT doing it.
        //
        // Scroll the host element, not the masthead: above 861px the masthead
        // is sticky, and scrollIntoView on a stuck element no-ops — which is
        // exactly why desktop never snapped. The host is static at every
        // width and sits at the top of the page, so with the theme's
        // scroll-padding-top this resolves to the page top — the same landing
        // the mobile flow always had, and the same call startBuilder makes.
        this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
      if (!options.silent) this.updateAll();
    }

    /* ---------- rendering ---------- */

    updateTabs() {
      if (!this.stepNav) return;
      const unlocked = this.highestUnlockedStep();
      this.stepNav.innerHTML = '';
      this.stepDefinitions.forEach((definition, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        const active = index === this.currentStep;
        const done = !active && !definition.review && this.stepHasSelection(definition);
        button.className = (active ? 'is-active' : '') + (done ? ' is-done' : '');
        button.disabled = index > unlocked;
        button.dataset.fkey = `tab:${definition.key}`;
        button.innerHTML = `${done ? '<span class="kbv2-tabs__check" aria-hidden="true">✓</span>' : ''}${this.escapeHtml(definition.label)}${done ? `<span class="visually-hidden">, ${this.escapeHtml(this.t('a11yDone', 'done'))}</span>` : ''}${definition.optional ? `<span class="kbv2-tabs__opt">${this.escapeHtml(this.t('optionalTag', 'Optional'))}</span>` : ''}`;
        if (active) button.setAttribute('aria-current', 'step');
        button.addEventListener('click', () => {
          this.focusKey = `tab:${definition.key}`;
          this.goToStep(index);
        });
        this.stepNav.appendChild(button);
      });

      this.text('[data-builder-mobile-step]', this.tpl('mobileStep', { n: this.currentStep + 1, m: this.stepDefinitions.length }, ''));
      // the step eyebrow already names the step in the content — the strip's
      // right side advertises the build sheet instead (the strip opens it)
      this.text('[data-builder-mobile-label]', `${this.t('mobileView', 'View build')} ›`);
      const bar = this.querySelector('[data-builder-mobile-bar]');
      if (bar) bar.style.width = `${Math.round(((this.currentStep + 1) / this.stepDefinitions.length) * 100)}%`;
    }

    heroCard() {
      const current = this.stepDefinitions[this.currentStep];
      if (current && !current.review && !current.assembly && current.key !== 'layout' && current.key !== 'keyboard') {
        const cards = this.selectedCards(current.key);
        if (cards.length) return cards[cards.length - 1];
      }
      const keyboard = this.firstSelected('keyboard');
      if (keyboard) return keyboard;
      for (const definition of this.productDefinitions()) {
        if (definition.assembly) continue;
        const cards = this.selectedCards(definition.key);
        if (cards.length) return cards[cards.length - 1];
      }
      return null;
    }

    updatePreview() {
      const empty = this.querySelector('[data-builder-visual-empty]');
      const board = this.querySelector('[data-builder-visual-board]');
      if (!empty || !board) return;
      const layoutName = this.layoutLabels[this.layout] || '';
      const currentKey = this.stepDefinitions[this.currentStep]?.key;

      // while sizing the board — the layout step, or the keyboard step before
      // one is chosen — the stage belongs to the layout itself; an unrelated
      // product photo (say, earlier-chosen keycaps) reads as a mistake
      if ((currentKey === 'layout' || currentKey === 'keyboard') && !this.firstSelected('keyboard')) {
        const layoutButton = this.layoutButtons.find((button) => button.dataset.builderLayout === this.layout);
        const layoutImage = layoutButton?.dataset.layoutImage || '';
        if (this.layout && layoutImage) {
          empty.hidden = true;
          board.hidden = false;
          const layoutImg = this.querySelector('[data-builder-visual-img]');
          if (layoutImg && layoutImg.getAttribute('src') !== layoutImage) {
            this.fadeSwap(layoutImg, () => { layoutImg.src = layoutImage; });
          }
          if (layoutImg) layoutImg.alt = layoutName;
          this.text('[data-builder-visual-cap]', layoutName);
          return;
        }
        empty.hidden = false;
        board.hidden = true;
        const layoutTitle = layoutName ? this.tpl('emptyLayoutTitle', { layout: layoutName }, `${layoutName} chosen`) : this.t('emptyTitle', '');
        const layoutSub = layoutName ? this.t('emptyLayoutSub', '') : this.t('emptySub', '');
        empty.innerHTML = `<strong>${this.escapeHtml(layoutTitle)}</strong><span>${this.escapeHtml(layoutSub)}</span>`;
        return;
      }

      const hero = this.heroCard();
      empty.hidden = Boolean(hero);
      board.hidden = !hero;
      if (!hero) {
        const title = layoutName ? this.tpl('emptyLayoutTitle', { layout: layoutName }, `${layoutName} chosen`) : this.t('emptyTitle', '');
        const sub = layoutName ? this.t('emptyLayoutSub', '') : this.t('emptySub', '');
        empty.innerHTML = `<strong>${this.escapeHtml(title)}</strong><span>${this.escapeHtml(sub)}</span>`;
        return;
      }

      const image = this.cardImage(hero);
      const img = this.querySelector('[data-builder-visual-img]');
      if (img && image && img.getAttribute('src') !== image) {
        this.fadeSwap(img, () => { img.src = image; });
      }
      if (img) img.alt = hero.dataset.productTitle || '';
      const names = [layoutName];
      this.productDefinitions().forEach((definition) => {
        if (definition.assembly || definition.selectionMode === 'multiple') return;
        const card = this.firstSelected(definition.key);
        if (card) names.push(card.dataset.productTitle);
      });
      this.text('[data-builder-visual-cap]', names.filter(Boolean).join(' · '));
    }

    updateSheet() {
      if (!this.sheet) return;
      this.sheet.innerHTML = '';
      const unlocked = this.highestUnlockedStep();

      this.choiceDefinitions().forEach((definition) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.dataset.fkey = `sheet:${definition.key}`;
        row.disabled = definition.index > unlocked;

        let name = '';
        let sub = '';
        let price = '';
        let image = '';
        let check = false;

        if (definition.key === 'layout') {
          name = this.layoutLabels[this.layout] || '';
          check = Boolean(name);
          const button = this.layoutButtons.find((candidate) => candidate.dataset.builderLayout === this.layout);
          image = button?.dataset.layoutImage || '';
        } else {
          const cards = this.selectedCards(definition.key);
          if (cards.length > 1) {
            name = this.tpl('multiSelected', { count: cards.length }, `${cards.length} selected`);
            const subtotal = cards.reduce((sum, card) => sum + Number(this.getVariant(card)?.price || 0), 0);
            price = this.formatMoney(subtotal);
            image = this.cardImage(cards[cards.length - 1]);
          } else if (cards.length === 1) {
            const card = cards[0];
            const variant = this.getVariant(card);
            name = card.dataset.productTitle || '';
            check = true;
            image = this.cardImage(card);
            if (definition.key === 'switches' && variant) {
              const packs = this.switchQuantity(card);
              const packSize = Number(card.dataset.packSize || 1);
              sub = packs === 1
                ? this.tpl('switchSubOne', { pack: packSize }, '')
                : this.tpl('switchSubMany', { packs, pack: packSize }, '');
              price = this.formatMoney(variant.price * packs);
            } else if (variant) {
              if (variant.title && !variant.title.toLowerCase().includes('default')) sub = variant.title;
              price = variant.price === 0 ? this.t('free', 'Free') : (variant.formattedPrice || this.formatMoney(variant.price));
            }
          }
        }

        row.className = `kbv2-sheet__row${name ? ' is-filled' : ' is-empty'}`;
        const thumb = image
          ? `<span class="kbv2-sheet__num kbv2-sheet__num--pic"><img src="${this.escapeAttribute(image)}" alt="" loading="lazy"></span>`
          : (check || name)
            ? '<span class="kbv2-sheet__num kbv2-sheet__num--done" aria-hidden="true">✓</span>'
            : '<span class="kbv2-sheet__num kbv2-sheet__num--slot" aria-hidden="true"></span>';
        row.innerHTML = `
          ${thumb}
          <span class="kbv2-sheet__what">
            <small>${this.escapeHtml(definition.label)}</small>
            <strong>${this.escapeHtml(name || this.t('sheetEmpty', 'Not chosen yet'))}</strong>
            ${sub ? `<em>${this.escapeHtml(sub)}</em>` : ''}
          </span>
          <span class="kbv2-sheet__price">${this.escapeHtml(price)}</span>`;
        row.addEventListener('click', () => {
          this.focusKey = `sheet:${definition.key}`;
          this.goToStep(definition.index);
        });
        this.sheet.appendChild(row);
      });

      this.text('[data-builder-panel-count]', this.tpl('panelProgress', {
        done: this.completedRequiredCount(),
        total: this.requiredDefinitions().length
      }, ''));
    }

    updateTotals() {
      const total = this.totalPrice();
      const shown = this.isPackage() ? Math.max(0, total - this.coveredAmount(total)) : total;
      this.text('[data-builder-mobile-total]', this.formatMoney(shown));
    }

    updatePackage() {
      const box = this.querySelector('[data-builder-pkg]');
      if (!box) return;
      if (!this.isPackage()) {
        box.hidden = true;
        return;
      }
      const total = this.totalPrice();
      const covered = this.coveredAmount(total);
      const capNote = this.overCap(total)
        ? (this.b2b.capBehavior === 'block'
          ? this.tpl('pkg.capBlock', { cap: this.formatMoney(this.b2b.cap) }, '')
          : this.tpl('pkg.capTopup', { cap: this.formatMoney(this.b2b.cap) }, ''))
        : '';
      const html = `
        <div class="kbv2-pkg__line"><span>${this.escapeHtml(this.t('pkg.totalLabel', 'Build total'))}</span><b>${this.escapeHtml(this.formatMoney(total))}</b></div>
        <div class="kbv2-pkg__line kbv2-pkg__line--covered"><span>${this.escapeHtml(this.tpl('pkg.coveredLabel', { client: this.b2b.client }, 'Covered'))}</span><b>−${this.escapeHtml(this.formatMoney(covered))}</b></div>
        <div class="kbv2-pkg__line kbv2-pkg__line--grand"><span>${this.escapeHtml(this.t('pkg.youPayLabel', 'You pay'))}</span><b>${this.escapeHtml(this.formatMoney(Math.max(0, total - covered)))}</b></div>
        ${this.t('pkg.checkoutNote', '') ? `<p class="kbv2-pkg__note">${this.escapeHtml(this.tpl('pkg.checkoutNote', { client: this.b2b.client }, ''))}</p>` : ''}
        ${capNote ? `<p class="kbv2-pkg__cap${this.b2b.capBehavior === 'block' ? ' kbv2-pkg__cap--block' : ''}">${this.escapeHtml(capNote)}</p>` : ''}`;
      // same no-op guard as the qty note: identical rewrites churn the DOM
      if (html !== this.lastPkgHtml) {
        this.lastPkgHtml = html;
        box.innerHTML = html;
      }
      box.hidden = false;
    }

    updateSwitchCardPrices() {
      const keyboard = this.firstSelected('keyboard');
      this.cardsFor('switches').forEach((card) => {
        const priceNode = card.querySelector('[data-builder-card-price]');
        if (!priceNode || card.dataset.priceHidden === 'true') return;
        const variant = this.getVariant(card);
        if (!variant) return;
        let tag = card.querySelector('.kbv2-card-tag--build');
        if (keyboard) {
          const packs = this.packsFor(Math.max(1, Number(card.dataset.packSize || 1)), this.requiredSwitches());
          priceNode.textContent = this.formatMoney(variant.price * packs);
          if (!tag) {
            tag = document.createElement('span');
            tag.className = 'kbv2-card-tag kbv2-card-tag--build';
            priceNode.parentElement?.insertBefore(tag, priceNode);
          }
          tag.textContent = this.t('forYourBuild', 'for your build');
          tag.hidden = false;
        } else {
          priceNode.textContent = variant.formattedPrice || this.formatMoney(variant.price);
          if (tag) tag.hidden = true;
        }
      });
    }

    updateQtyNote() {
      const note = this.querySelector('[data-builder-qty-note]');
      if (!note) return;
      const keyboard = this.firstSelected('keyboard');
      const switchCard = this.firstSelected('switches');
      if (!keyboard || !switchCard) {
        note.hidden = true;
        return;
      }
      const keys = this.requiredSwitches();
      const packs = this.switchQuantity(switchCard);
      const packSize = Math.max(1, Number(switchCard.dataset.packSize || 1));
      const supplied = packs * packSize;
      const variant = this.getVariant(switchCard);
      note.hidden = false;
      const html = `
        <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true"><path d="m3 8 3 3 7-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <div class="kbv2-qty-note__body">
          ${this.escapeHtml(
            this.tpl('qtySentence', {
              total: supplied,
              keys,
              keyboard: keyboard.dataset.productTitle || '',
              layout: this.layoutLabels[this.layout] || '',
              spares: Math.max(0, supplied - keys)
            }, '')
            // the spares clause is its own sentence so exact-fit packs never
            // read "plus 0 spares"
            + (supplied - keys > 0 ? ` ${this.tpl('qtySentenceSpares', { spares: supplied - keys }, 'That leaves {spares} in reserve.')}` : '')
          )}
          <details class="kbv2-qty-note__math">
            <summary>${this.escapeHtml(this.t('qtyHow', 'How we count'))}</summary>
            <div>${this.escapeHtml(
              this.tpl('qtyMath', {
                keys,
                packs,
                pack: packSize,
                price: variant ? (variant.formattedPrice || this.formatMoney(variant.price)) : ''
              }, '')
              + (this.minSpares === 1 ? ` ${this.tpl('qtyMathSparesOne', { min: 1 }, '')}` : this.minSpares > 0 ? ` ${this.tpl('qtyMathSpares', { min: this.minSpares }, '')}` : '')
            )}</div>
          </details>
        </div>`;
      if (html !== this.lastQtyNoteHtml) {
        this.lastQtyNoteHtml = html;
        note.innerHTML = html;
      }
    }

    updateServiceImages() {
      if (!this.assemblyDefinition) return;
      const keyboard = this.firstSelected('keyboard');
      const keyboardImage = keyboard ? this.cardImage(keyboard) : '';
      this.cardsFor(this.assemblyDefinition.key).forEach((card) => {
        const image = card.querySelector('.ktechs-builder-card__image');
        if (!image) return;
        const target = keyboardImage || card.dataset.origImage || '';
        if (target && image.getAttribute('src') !== target) {
          this.fadeSwap(image, () => {
            image.src = target;
            image.srcset = target;
            image.removeAttribute('data-src');
            image.removeAttribute('data-srcset');
          });
        }
      });
    }

    // gentle image crossfade: fade out, swap, fade back in on load — instant
    // under prefers-reduced-motion or on an image that hasn't rendered yet
    fadeSwap(img, apply) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !img.getAttribute('src')) {
        apply();
        return;
      }
      clearTimeout(img._kbv2Swap);
      img.style.opacity = '0';
      img._kbv2Swap = setTimeout(() => {
        const reveal = () => {
          img.style.opacity = '1';
          img.removeEventListener('load', reveal);
        };
        img.addEventListener('load', reveal);
        apply();
        if (img.complete) reveal();
      }, 170);
    }

    updateReview() {
      const container = this.querySelector('[data-builder-review-lines]');
      if (!container) return;
      container.innerHTML = '';

      const addLine = (label, name, sub, price, image, stepIndex) => {
        const line = document.createElement('div');
        line.className = image ? 'kbv2-rv-line' : 'kbv2-rv-line kbv2-rv-line--noimg';
        line.innerHTML = `
          ${image ? `<span class="kbv2-rv-line__img"><img src="${this.escapeAttribute(image)}" alt="" loading="lazy"></span>` : ''}
          <span class="kbv2-rv-line__what">
            <small>${this.escapeHtml(label)}</small>
            <strong>${this.escapeHtml(name)}</strong>
            ${sub ? `<em>${this.escapeHtml(sub)}</em>` : ''}
            <button type="button" class="kbv2-rv-line__edit" aria-label="${this.escapeAttribute(`${this.t('reviewEdit', 'Edit')} ${label}: ${name}`)}">${this.escapeHtml(this.t('reviewEdit', 'Edit'))}</button>
          </span>
          <span class="kbv2-rv-line__price">${this.escapeHtml(price)}</span>`;
        line.querySelector('button').addEventListener('click', () => {
          // the button destroys itself in the re-render — hand focus to the
          // destination step's tab like every sibling control does
          this.focusKey = `tab:${this.stepDefinitions[stepIndex]?.key}`;
          this.goToStep(stepIndex);
        });
        container.appendChild(line);
      };

      const layoutDefinition = this.definitionFor('layout');
      if (layoutDefinition && this.layout) {
        addLine(layoutDefinition.label, this.layoutLabels[this.layout] || '', '', '', '', layoutDefinition.index);
      }

      this.productDefinitions().forEach((definition) => {
        this.selectedCards(definition.key).forEach((card) => {
          const variant = this.getVariant(card);
          let sub = '';
          let price = '';
          if (definition.key === 'switches' && variant) {
            const packs = this.switchQuantity(card);
            const packSize = Number(card.dataset.packSize || 1);
            sub = packs === 1 ? this.tpl('switchSubOne', { pack: packSize }, '') : this.tpl('switchSubMany', { packs, pack: packSize }, '');
            price = this.formatMoney(variant.price * packs);
          } else if (variant) {
            if (variant.title && !variant.title.toLowerCase().includes('default')) sub = variant.title;
            price = variant.price === 0 ? this.t('free', 'Free') : (variant.formattedPrice || this.formatMoney(variant.price));
          }
          addLine(definition.label, card.dataset.productTitle || '', sub, price, this.cardImage(card), definition.index);
        });
      });

      const reviewBlock = this.stepDefinitions.find((definition) => definition.review)?.block;
      if (reviewBlock) {
        let extras = reviewBlock.querySelector('.kbv2-rv-extras');
        if (!extras) {
          extras = document.createElement('div');
          extras.className = 'kbv2-rv-extras';
          container.insertAdjacentElement('afterend', extras);
        }
        const complete = this.allRequiredComplete();
        extras.innerHTML = `
          ${complete ? `<div class="kbv2-rv-complete"><b>✓ ${this.escapeHtml(this.t('reviewComplete', 'Complete build'))}</b><span>${this.escapeHtml(this.t('reviewCompleteMsg', ''))}</span></div>` : ''}
          ${this.t('reviewTerms', '') ? `<p class="kbv2-rv-terms">${this.escapeHtml(this.t('reviewTerms', ''))}</p>` : ''}`;
        const conflict = this.cartConflict();
        if (conflict) {
          const chosen = this.conflictChoice;
          const choice = (value, label) => `<button type="button" class="kbv2-rv-conflict__choice" data-builder-conflict="${value}" aria-pressed="${chosen === value}">${this.escapeHtml(label)}</button>`;
          extras.insertAdjacentHTML('beforeend', `
            <div class="kbv2-rv-conflict" role="group" aria-label="${this.escapeAttribute(this.t('conflictChoose', 'Choose one to continue.'))}">
              <p><b>${this.escapeHtml(this.tpl('conflictInBuild', { name: conflict.name, id: conflict.buildId }, '{name} is already in your cart as build {id}.'))}</b> ${this.escapeHtml(this.t('conflictChoose', 'Choose one to continue.'))}</p>
              <div class="kbv2-rv-conflict__choices">${choice('replace', this.t('conflictReplace', 'Replace that build'))}${choice('second', this.t('conflictSecond', 'Add as a second keyboard'))}</div>
              <small>${this.escapeHtml(this.t('conflictReplaceHelp', 'Replacing removes every part of that build from your cart.'))}</small>
            </div>`);
          extras.querySelectorAll('[data-builder-conflict]').forEach((node) => node.addEventListener('click', () => {
            this.conflictChoice = node.dataset.builderConflict;
            this.updateAll();
          }));
        }
      }
    }

    updateActions() {
      const definition = this.stepDefinitions[this.currentStep];
      if (!definition || !this.nextButton) return;
      const satisfied = this.stepSatisfied(definition);
      const isReview = Boolean(definition.review);

      const blocked = this.capBlocked();
      // hard cap gates only the final action — the customer can still browse
      // and reach Review, where the cap note says exactly what to change
      const undecided = isReview && Boolean(this.cartConflict()) && !this.conflictChoice;
      this.nextButton.disabled = !satisfied || (blocked && isReview) || undecided;
      this.backButton.hidden = this.currentStep === 0;
      const label = isReview ? this.t('addToCart', 'Add complete build to cart') : this.t('continue', 'Continue');
      const nextLabel = this.nextButton.querySelector('span:first-child');
      if (nextLabel) nextLabel.textContent = label;

      const status = this.querySelector('[data-builder-action-status]');
      if (!status) return;
      const statusText = blocked ? this.t('pkg.blockedStatus', 'Over the package budget. Swap an item to continue.')
        : isReview ? this.t('reviewStatus', '')
        : this.stepHasSelection(definition) ? this.tpl('chosenStatus', { step: definition.label }, '')
        : definition.optional ? this.tpl('optionalStatus', { step: definition.label }, '')
        : this.t('chooseStatus', '');
      // same no-op guard as text(): identical rewrites re-announce on some SRs
      if (status.textContent !== statusText) status.textContent = statusText;
    }

    updateStartScreen() {
      // no gate any more — the page lands on the Layout step; `started` only
      // remains as the storage flag older saves are validated against
      const ui = this.querySelector('[data-builder-ui]');
      if (ui) ui.hidden = !this.started;
      // rail (in the masthead) + the sticky mobile strip live outside kbv2-ui
      const nav = this.querySelector('[data-builder-step-nav]');
      if (nav) nav.hidden = !this.started;
      const progress = this.querySelector('[data-builder-mobile-progress]');
      if (progress) progress.hidden = !this.started;
    }

    updateAll() {
      if (this.isHydrating) return;
      this.updateStartScreen();
      this.updateStrip();
      this.updateTabs();
      this.applyStepVisibility();
      this.updateLens();
      this.updatePreview();
      this.updateSheet();
      this.updateTotals();
      this.updatePackage();
      this.updateSwitchCardPrices();
      this.updateQtyNote();
      this.updateServiceImages();
      this.updateReview();
      this.updateActions();
      this.updateUrl();
      this.saveState();
      this.hideError();
      this.restoreFocus();
      this.syncMastheadHeight();
    }

    restoreFocus() {
      if (!this.focusKey) return;
      const key = this.focusKey;
      this.focusKey = null;
      let element = this.querySelector(`[data-fkey="${CSS.escape(key)}"]`);
      // the last "Show more" click hides the button itself — hand focus to
      // the last revealed card instead of dropping it on <body>
      if (element && (element.disabled || element.offsetParent === null) && key.startsWith('loadmore:')) {
        const stepKey = key.split(':')[1];
        const definition = this.stepDefinitions.find((candidate) => candidate.key === stepKey);
        const cards = definition ? Array.from(definition.block.querySelectorAll('[data-builder-item]')).filter((card) => card.offsetParent) : [];
        element = cards.length ? cards[cards.length - 1].querySelector('[data-builder-select]') : null;
      }
      if (element && !element.disabled) element.focus({ preventScroll: true });
    }

    /* ---------- url + persistence ---------- */

    updateUrl() {
      if (this.isHydrating || !this.started || window.Shopify?.designMode) return;
      const url = new URL(window.location.href);
      if (this.codesEnabled() && this.hasBuildState()) {
        url.searchParams.set('build', this.ensureBuildCode());
        Array.from(url.searchParams.keys()).filter((key) => key.startsWith('builder_')).forEach((key) => url.searchParams.delete(key));
        try {
          window.history.replaceState({}, '', url.toString());
        } catch (_error) { /* Safari rate-limits history writes — autosave still carries the state */ }
        return;
      }
      url.searchParams.delete('build');
      if (this.layout) url.searchParams.set('builder_layout', this.layout);
      else url.searchParams.delete('builder_layout');

      this.productDefinitions().forEach((definition) => {
        const cards = this.selectedCards(definition.key);
        const handles = cards.map((card) => card.dataset.productHandle).filter(Boolean);
        const variants = cards.map((card) => this.getVariant(card)?.id).filter(Boolean);
        if (handles.length && variants.length) {
          url.searchParams.set(`builder_${definition.key}`, handles.join(','));
          url.searchParams.set(`builder_${definition.key}_variant`, variants.join(','));
        } else {
          url.searchParams.delete(`builder_${definition.key}`);
          url.searchParams.delete(`builder_${definition.key}_variant`);
        }
      });
      try {
        window.history.replaceState({}, '', url.toString());
      } catch (_error) { /* Safari rate-limits history writes — autosave still carries the state */ }
    }

    storageKey() {
      return `ktechs-builder-v2:${window.location.pathname}`;
    }

    snapshotState() {
      return {
        layout: this.layout,
        step: this.currentStep,
        selections: Object.fromEntries(this.productDefinitions().map((definition) => [
          definition.key,
          this.selectedCards(definition.key).map((card) => ({
            handle: card.dataset.productHandle || '',
            variant: this.getVariant(card)?.id || null
          }))
        ]))
      };
    }

    // one validating apply for every restore path (autosave hydrate, set-aside
    // undo, layout/keyboard-change undo): skip missing/sold-out/unstocked
    // entries and collect their names — never select what the grid can't show
    applySelections(selections) {
      const dropped = [];
      this.productDefinitions().forEach((definition) => {
        (selections?.[definition.key] || []).forEach((entry) => {
          const card = this.findCard(definition.key, entry.handle, entry.variant);
          if (!card) {
            const title = this.productTitleFor(definition.key, entry.handle);
            if (title) dropped.push(title);
            return;
          }
          if (definition.key === 'keyboard' && this.layout && card.dataset.layoutKey !== this.layout) return;
          const variant = this.getVariant(card);
          if (!variant || !variant.available || card.dataset.stocked === 'false') {
            if (card.dataset.productTitle) dropped.push(card.dataset.productTitle);
            return;
          }
          this.selectCard(card, { silent: true });
        });
      });
      return dropped;
    }

    restoreState(snapshot) {
      this.clearAll();
      if (snapshot.layout) this.selectLayout(snapshot.layout, { silent: true });
      const dropped = this.applySelections(snapshot.selections);
      this.filterLayout();
      this.filterCompatibility({ silent: true }).forEach((card) => {
        if (card.dataset.productTitle) dropped.push(card.dataset.productTitle);
      });
      this.goToStep(snapshot.step ?? this.currentStep, { force: true, silent: true });
      this.updateAll();
      if (dropped.length) {
        setTimeout(() => this.showToast(this.tpl('soldOutRestore', { name: this.joinNames([...new Set(dropped)]) }, '{name} sold out since your last visit, so we took it out of the build.')), 400);
      }
    }

    saveState() {
      this.persistLocal();
      this.queueRemoteSave();
    }

    persistLocal() {
      if (this.isHydrating || !this.started || window.Shopify?.designMode) return;
      try {
        window.localStorage.setItem(this.storageKey(), JSON.stringify({
          ...this.snapshotState(),
          started: true,
          code: this.buildCode || null,
          token: this.buildToken || null,
          codeUsed: Boolean(this.codeUsed)
        }));
      } catch (_error) { /* storage unavailable */ }
    }

    // an autosave with no layout and no picks is not a build — nothing to set aside
    savedHasBuild(saved) {
      if (!saved) return false;
      if (saved.layout) return true;
      return Object.values(saved.selections || {}).some((items) => Array.isArray(items) && items.length > 0);
    }

    loadSavedState() {
      try {
        const raw = window.localStorage.getItem(this.storageKey());
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (!saved || !saved.started) return null;
        return saved;
      } catch (_error) {
        return null;
      }
    }

    hydrate() {
      // the page always opens on the step flow now — no start gate
      this.started = true;
      if (window.Shopify?.designMode) return;

      const params = new URLSearchParams(window.location.search);
      const remoteCode = (params.get('build') || '').trim().toUpperCase();
      const ownCode = this.loadSavedState()?.code;
      if (this.codesEnabled() && this.isBuildCode(remoteCode) && remoteCode !== ownCode) {
        // resolved by restoreRemoteBuild() once the page is up; the visitor's
        // own autosave gets the same set-aside offer a long link gives it
        this.pendingRemoteCode = remoteCode;
        return;
      }
      const hasUrlState = Array.from(params.keys()).some((key) => key.startsWith('builder_'));
      if (hasUrlState) {
        const saved = this.loadSavedState();
        const dropped = this.restoreFromUrl(params);
        if (this.savedHasBuild(saved)) {
          const snapshot = this.snapshotState();
          const differs = JSON.stringify({ l: saved.layout, s: saved.selections })
            !== JSON.stringify({ l: snapshot.layout, s: snapshot.selections });
          // a share/featured link must not silently destroy the visitor's own
          // autosaved build — offer the way back before the first save overwrites it
          if (differs) this.pendingSavedRestore = saved;
        }
        if (dropped.length) {
          if (this.pendingSavedRestore) {
            // the set-aside toast owns the single toast slot and its Undo is
            // the only surviving copy of the old build — fold the sold-out
            // names into it instead of clobbering it 400ms later
            this.pendingDroppedNames = dropped;
          } else {
            setTimeout(() => this.showToast(this.tpl('restoreDropped', { name: this.joinNames(dropped) }, '{name} couldn’t be restored from this link, so it’s not in the build.')), 400);
          }
        }
        return;
      }

      this.hydrateFromSaved();
    }

    hydrateFromSaved() {
      const saved = this.loadSavedState();
      if (!saved) return;
      this.buildCode = this.isBuildCode(saved.code) ? saved.code : null;
      this.buildToken = typeof saved.token === 'string' ? saved.token : null;
      this.codeUsed = Boolean(saved.codeUsed);
      const dropped = [];
      if (saved.layout && this.layoutLabels[saved.layout]) this.selectLayout(saved.layout, { silent: true });

      dropped.push(...this.applySelections(saved.selections));

      const removedByCompat = this.filterCompatibility({ silent: true });
      removedByCompat.forEach((card) => {
        if (card.dataset.productTitle) dropped.push(card.dataset.productTitle);
      });

      if (!this.layout && !this.firstSelected('keyboard')) return;
      this.currentStep = Math.max(0, Math.min(this.highestUnlockedStep(), Number(saved.step) || 0));
      this.showResumeBar();
      if (dropped.length) {
        setTimeout(() => this.showToast(this.tpl('soldOutRestore', { name: this.joinNames(dropped) }, '{name} sold out since your last visit, so we took it out of the build.')), 400);
      }
    }

    restoreFromUrl(params) {
      const dropped = [];
      const layout = params.get('builder_layout');
      // a renamed/stale layout key must not half-restore: without a real
      // layout, the keyboard below is dropped (and named) instead of admitted
      const layoutKnown = !layout || Boolean(this.layoutLabels[layout]);
      if (layout && layoutKnown) this.selectLayout(layout, { silent: true });
      this.productDefinitions().forEach((definition) => {
        const handles = (params.get(`builder_${definition.key}`) || '').split(',').filter(Boolean);
        const variants = (params.get(`builder_${definition.key}_variant`) || '').split(',').filter(Boolean);
        handles.forEach((handle, index) => {
          const card = this.findCard(definition.key, handle, variants[index]);
          if (!card) {
            // unknown product, or the exact shared variant no longer exists
            const title = this.productTitleFor(definition.key, handle);
            if (title) dropped.push(title);
            return;
          }
          if (card.dataset.stocked === 'false') {
            if (card.dataset.productTitle) dropped.push(card.dataset.productTitle);
            return;
          }
          if (definition.key === 'keyboard' && (!layoutKnown || (this.layout && card.dataset.layoutKey !== this.layout))) {
            if (card.dataset.productTitle) dropped.push(card.dataset.productTitle);
            return;
          }
          this.selectCard(card, { silent: true });
        });
      });
      // parity with the saved-state path: incompatible parts are reported,
      // never silently discarded
      this.filterCompatibility({ silent: true }).forEach((card) => {
        if (card.dataset.productTitle) dropped.push(card.dataset.productTitle);
      });
      this.currentStep = Math.min(this.highestUnlockedStep(), this.stepDefinitions.length - 1);
      return dropped;
    }

    showResumeBar() {
      const bar = this.querySelector('[data-builder-resume]');
      if (!bar) return;
      bar.hidden = false;
      bar.innerHTML = `<span>${this.escapeHtml(this.t('resume', 'Welcome back. Your last build is still here.'))}</span>`;
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = this.t('resumeReset', 'Start over');
      button.addEventListener('click', () => {
        const snapshot = this.snapshotState();
        this.clearAll();
        try { window.localStorage.removeItem(this.storageKey()); } catch (_error) { /* noop */ }
        this.currentStep = 0;
        bar.hidden = true;
        this.goToStep(0, { force: true, silent: true });
        this.updateAll();
        this.showToast(this.t('resumeCleared', 'Build cleared.'), () => {
          bar.hidden = false;
          this.restoreState(snapshot);
        });
      });
      bar.appendChild(button);
    }

    /* ---------- drawer ---------- */

    openDrawer() {
      if (this.classList.contains('drawer-open')) return;
      this.drawerReturnFocus = document.activeElement;
      this.classList.add('drawer-open');
      const panel = this.querySelector('[data-builder-panel]');
      panel?.setAttribute('role', 'dialog');
      panel?.setAttribute('aria-modal', 'true');
      const title = panel?.querySelector('[data-builder-panel-title]');
      if (title) {
        if (!title.id) title.id = `kbv2-panel-title-${this.dataset.sectionId || 'main'}`;
        panel.setAttribute('aria-labelledby', title.id);
      }
      this.syncDrawer();
      // focus after the drawer paints, and never let the browser scroll the
      // page chasing a fixed element mid-transition (it jumped to the footer)
      requestAnimationFrame(() => {
        this.querySelector('[data-builder-drawer-close]')?.focus({ preventScroll: true });
      });
    }

    closeDrawer() {
      if (!this.classList.contains('drawer-open')) return;
      this.classList.remove('drawer-open');
      const panel = this.querySelector('[data-builder-panel]');
      panel?.removeAttribute('role');
      panel?.removeAttribute('aria-modal');
      this.syncDrawer();
      const target = this.drawerReturnFocus && this.contains(this.drawerReturnFocus)
        ? this.drawerReturnFocus
        : this.querySelector('[data-builder-mobile-progress]');
      target?.focus({ preventScroll: true });
      this.drawerReturnFocus = null;
    }

    syncDrawer() {
      const open = this.classList.contains('drawer-open');
      this.querySelector('[data-builder-mobile-progress]')?.setAttribute('aria-expanded', String(open));
      const scrim = this.querySelector('[data-builder-scrim]');
      if (scrim) scrim.hidden = !open;
    }

    handleKeydown(event) {
      if (!this.classList.contains('drawer-open')) return;
      if (event.key === 'Escape') { this.closeDrawer(); return; }
      if (event.key !== 'Tab' || !window.matchMedia('(max-width: 860px)').matches) return;
      const panel = this.querySelector('[data-builder-panel]');
      if (!panel) return;
      // the pinned bottom bar reads as the drawer's own footer while open —
      // the trap must reach it, or keyboard users see a Continue they can
      // never Tab to
      const scope = [panel, this.querySelector('[data-builder-mobilebar]')].filter(Boolean);
      const focusables = scope
        .flatMap((root) => Array.from(root.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')))
        .filter((el) => el.offsetParent && !el.disabled);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      else if (!scope.some((root) => root.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    }

    handleThemeBlockSelect(event) {
      if (!window.Shopify?.designMode) return;
      const featured = event.target?.closest?.('[data-builder-featured-build]');
      if (featured && this.contains(featured)) {
        const strip = this.querySelector('[data-builder-strip]');
        if (strip && !strip.hidden) strip.scrollIntoView({ block: 'center' });
        return;
      }
      const stepBlock = event.target?.closest?.('[data-builder-step-block]');
      if (!stepBlock || !this.contains(stepBlock)) return;
      this.started = true;
      const index = this.stepDefinitions.findIndex((definition) => definition.block === stepBlock);
      if (index >= 0) this.goToStep(index, { force: true, silent: true });
      this.updateAll();
      // selecting an item block scrolls its card into view (when the card is
      // hidden — sold out, no product — the diagnostics panel says why)
      const itemCard = event.target?.closest?.('.ktechs-builder__theme-item')?.querySelector('[data-builder-item]');
      if (itemCard && !itemCard.hidden) itemCard.scrollIntoView({ block: 'center' });
    }

    /* ---------- primary action + cart ---------- */

    handlePrimaryAction() {
      const definition = this.stepDefinitions[this.currentStep];
      if (!definition || !this.stepSatisfied(definition)) return;
      if (definition.review) this.addBuildToCart();
      else this.goToStep(this.currentStep + 1);
    }

    /* ---------- build codes (share links) ---------- */
    // The code that ends up on every cart line of a build (KB-XXXXXXXX-XXXX)
    // is assigned at the first change, so the address bar can carry
    // ?build=CODE from the start and a link copied at any moment resolves.
    // The state behind a code lives in the ktechs-build-codes Worker
    // (merchant setting "Build code service URL"); blank, unreachable or in
    // the theme editor = the long-form link, exactly as before. A code stays
    // on a bought build until the customer changes something, then a fresh
    // one starts — one code, one build, all the way to the order.
    codesEnabled() {
      return Boolean((this.dataset.buildCodesUrl || '').trim()) && !this.remoteUnavailable && !window.Shopify?.designMode;
    }

    codesBase() {
      return (this.dataset.buildCodesUrl || '').trim().replace(/\/+$/, '');
    }

    isBuildCode(code) {
      return /^KB-[A-Z0-9]{6,12}-[A-Z0-9]{4}$/.test(String(code || ''));
    }

    newBuildCode() {
      return `KB-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    }

    ensureBuildCode() {
      if (!this.isBuildCode(this.buildCode)) {
        this.buildCode = this.newBuildCode();
        this.buildToken = null;
        this.codeUsed = false;
      }
      return this.buildCode;
    }

    hasBuildState() {
      return Boolean(this.layout) || this.productDefinitions().some((definition) => this.selectedCards(definition.key).length > 0);
    }

    remoteState() {
      const snapshot = this.snapshotState();
      return { layout: snapshot.layout, selections: snapshot.selections };
    }

    queueRemoteSave() {
      if (!this.codesEnabled() || !this.started || this.isHydrating || !this.hasBuildState()) return;
      clearTimeout(this.remoteSaveTimer);
      this.remoteSaveTimer = setTimeout(() => { this.saveRemote(); }, 1500);
    }

    // Returns true when the store holds the current build under this.buildCode.
    // One save at a time: a debounced save racing the add-to-cart save on a
    // fresh code would make the loser 403 and fork mid-add.
    async saveRemote(options = {}) {
      if (this.remoteSaving) await this.remoteSaving.catch(() => false);
      this.remoteSaving = this.saveRemoteNow(options);
      try {
        return await this.remoteSaving;
      } finally {
        this.remoteSaving = null;
      }
    }

    async saveRemoteNow(options = {}) {
      if (!this.codesEnabled() || !this.hasBuildState()) return false;
      clearTimeout(this.remoteSaveTimer);
      const state = this.remoteState();
      const signature = JSON.stringify(state);
      // the code on a bought build is final: the first change after an add
      // starts a fresh code, so the shared link keeps naming what was bought
      if (this.codeUsed && signature !== this.lastSavedSignature) {
        this.buildCode = null;
        this.buildToken = null;
        this.codeUsed = false;
      }
      const code = this.ensureBuildCode();
      if (signature === this.lastSavedSignature && code === this.lastSavedCode) return true;
      try {
        const response = await fetch(`${this.codesBase()}/builds/${code}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(this.buildToken ? { 'X-Build-Token': this.buildToken } : {}) },
          body: signature
        });
        if (response.status === 403 && !this.buildToken && !options.forked) {
          // a code we did not create (a link someone opened): fork onto our own
          this.buildCode = null;
          this.buildToken = null;
          return this.saveRemoteNow({ forked: true });
        }
        if (!response.ok) return this.remoteFailed();
        const data = await response.json().catch(() => ({}));
        if (data.token) this.buildToken = data.token;
        this.lastSavedSignature = signature;
        this.lastSavedCode = code;
        this.persistLocal();
        this.updateUrl();
        return true;
      } catch (_error) {
        return this.remoteFailed();
      }
    }

    // the store did not take a save: never leave a code in the address bar
    // that nothing can resolve — long-form links for the rest of the session
    remoteFailed() {
      this.remoteUnavailable = true;
      this.updateUrl();
      return false;
    }

    async restoreRemoteBuild() {
      const code = this.pendingRemoteCode;
      if (!code) return;
      this.pendingRemoteCode = null;
      let data = null;
      try {
        const response = await fetch(`${this.codesBase()}/builds/${code}`, { headers: { Accept: 'application/json' } });
        if (response.ok) data = await response.json();
      } catch (_error) { /* unreachable store: treated as unknown code */ }
      if (!data?.state) {
        this.isHydrating = true;
        this.hydrateFromSaved();
        this.isHydrating = false;
        this.goToStep(this.currentStep, { force: true, silent: true });
        this.updateAll();
        this.showToast(this.t('buildLinkUnavailable', 'This build link could not be loaded. Start a new build, or try the link again later.'), null, { sticky: true });
        return;
      }
      const saved = this.loadSavedState();
      this.isHydrating = true;
      this.clearAll();
      if (data.state.layout && this.layoutLabels[data.state.layout]) this.selectLayout(data.state.layout, { silent: true });
      const dropped = this.applySelections(data.state.selections || {});
      this.filterCompatibility({ silent: true }).forEach((card) => {
        if (card.dataset.productTitle) dropped.push(card.dataset.productTitle);
      });
      this.isHydrating = false;
      // whoever opened this link edits a FORK: their saves go to a fresh code,
      // and the Worker refuses writes to the original without its token anyway
      this.buildCode = null;
      this.buildToken = null;
      this.codeUsed = false;
      this.lastSavedSignature = null;
      this.currentStep = Math.max(0, Math.min(this.highestUnlockedStep(), this.stepDefinitions.length - 1));
      this.goToStep(this.currentStep, { force: true, silent: true });
      this.updateAll();
      if (this.savedHasBuild(saved)) {
        const snapshot = this.snapshotState();
        const differs = JSON.stringify({ l: saved.layout, s: saved.selections }) !== JSON.stringify({ l: snapshot.layout, s: snapshot.selections });
        if (differs) {
          let message = this.t('linkReplaced', 'This link opened a shared build. Your own saved build was set aside.');
          if (dropped.length) message = `${this.tpl('restoreDropped', { name: this.joinNames(dropped) }, '{name} couldn’t be restored from this link, so it’s not in the build.')} ${message}`;
          this.showToast(message, () => this.restoreState(saved));
          return;
        }
      }
      if (dropped.length) this.showToast(this.tpl('restoreDropped', { name: this.joinNames(dropped) }, '{name} couldn’t be restored from this link, so it’s not in the build.'));
    }

    /* ---------- the cart the customer already has ---------- */
    // The rail hands a carted board to the builder ("Finish it in the builder"),
    // and the drawer's assembly checkbox may already have put the paid service
    // in the cart. Before 5 Sep the builder knew none of this: it re-added the
    // carted board (Shopify: "The maximum quantity of this item is already in
    // your cart" at stock 1, a silent duplicate at stock 2+) and re-asked the
    // assembly question from scratch. Now the builder reads the cart on open
    // and adopts what is already there: the assembly step opens on the cart's
    // answer, and a carted board or service is stamped into the build instead
    // of added again. Merchant ruling: every flow ends in the same cart.
    async fetchCart() {
      try {
        const response = await fetch('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } });
        return response.ok ? await response.json() : null;
      } catch (_error) {
        return null;
      }
    }

    async adoptCart() {
      const cart = await this.fetchCart();
      if (!cart || !Array.isArray(cart.items)) return;
      this.cartSnapshot = cart;
      this.conflictChoice = null;
      // LOOSE lines (no build id) are hand-over material: a board carted from
      // its page, the service ticked in the drawer. Lines inside a build are
      // OWNED by that build and are never adopted — a second build with the
      // same board once stole it and left the first build's parts orphaned.
      const loose = new Set();
      const owned = new Map();
      const adoptedKeyboard = this.firstSelected('keyboard');
      const adoptedKeyboardId = adoptedKeyboard ? String(this.getVariant(adoptedKeyboard)?.id || '') : '';
      cart.items.forEach((line) => {
        const build = line.properties?._ktechs_build_id;
        if (!build) {
          // assembly folded under ANOTHER keyboard is that keyboard's answer, not ours
          const forId = String(line.properties?._ktechs_for || '');
          if (forId && adoptedKeyboardId && forId !== adoptedKeyboardId) return;
          loose.add(String(line.variant_id));
          return;
        }
        owned.set(String(line.variant_id), String(build));
        // a merged bundle (cart transform) hides its parts: the engine lists
        // their variant ids on the parent line as _ktechs_components
        this.bundleComponents(line).forEach((id) => owned.set(id, String(build)));
      });
      this.cards.forEach((card) => {
        const variant = this.getVariant(card);
        const id = variant ? String(variant.id) : '';
        card.dataset.inCart = loose.has(id) ? 'true' : 'false';
        if (owned.has(id)) card.dataset.inBuild = owned.get(id);
        else delete card.dataset.inBuild;
      });
      if (!this.assemblyDefinition) return;
      // a loose assembly line is the drawer checkbox's answer — the step opens on it
      const carted = this.cardsFor(this.assemblyDefinition.key)
        .find((card) => card.dataset.inCart === 'true' && card.dataset.stocked !== 'false');
      if (carted && this.firstSelected(this.assemblyDefinition.key) !== carted) {
        this.selectCard(carted, { silent: true });
        this.updateAll();
      }
    }

    bundleComponents(line) {
      return String(line?.properties?._ktechs_components || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    }

    // the chosen board already lives in ANOTHER build in the cart (as its own
    // line, or inside that build's merged bundle)
    cartConflict(cart = this.cartSnapshot) {
      const keyboard = this.firstSelected('keyboard');
      const variant = keyboard ? this.getVariant(keyboard) : null;
      if (!variant || !cart || !Array.isArray(cart.items)) return null;
      const wanted = String(variant.id);
      const line = cart.items.find((candidate) => candidate.properties?._ktechs_build_id
        && (String(candidate.variant_id) === wanted || this.bundleComponents(candidate).includes(wanted)));
      if (!line) return null;
      const buildId = String(line.properties._ktechs_build_id);
      return {
        buildId,
        name: keyboard.dataset.productTitle || '',
        lines: cart.items.filter((candidate) => String(candidate.properties?._ktechs_build_id || '') === buildId)
      };
    }

    cartErrorMessage(state) {
      // /cart/add answers with `errors`, /cart/add.js with `description` — read both
      const errors = state?.errors;
      const fromErrors = typeof errors === 'string' ? errors : (Array.isArray(errors) ? errors.join(' ') : '');
      return state?.description || state?.message || fromErrors || this.t('errorGeneric', '');
    }

    async changeCartLine(payload) {
      const response = await fetch(window.theme?.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
      const state = await response.json().catch(() => ({}));
      if (!response.ok || state.status) throw new Error(this.cartErrorMessage(state));
      return state;
    }

    async addBuildToCart() {
      const entries = this.selectedEntries();
      if (!entries.length || !this.allRequiredComplete() || this.capBlocked()) return;
      const invalid = entries.find((entry) => !entry.variant.available);
      if (invalid) {
        this.showError(this.tpl('errorUnavailable', { name: invalid.card.dataset.productTitle || '' }, this.t('errorGeneric', '')));
        return;
      }

      // a bought build keeps its code; anything added after it is a new build
      // with a new code — with or without the code store
      if (this.codeUsed) {
        this.buildCode = null;
        this.buildToken = null;
        this.codeUsed = false;
      }
      const buildId = this.ensureBuildCode();
      if (this.codesEnabled()) await this.saveRemote(); // the shared code names the build as bought — best effort
      const keyboardTitle = this.firstSelected('keyboard')?.dataset.productTitle || '';
      // an assembly step whose free variant vanished lets a build reach the
      // cart with no service line at all — say so explicitly on the lines so
      // fulfillment reads the gap instead of guessing (audit F09)
      const hasService = this.assemblyDefinition
        ? entries.some((entry) => entry.component === this.assemblyDefinition.key)
        : true;
      // visible (non-underscore) properties surface the build on cart lines,
      // checkout, order emails and packing slips
      const buildLabel = this.t('cartBuildLabel', '');
      // the bundle line shows the chosen keyboard: its image rides the
      // keyboard line as _ktechs_image (the engine only honours CDN URLs)
      const cardImage = (card, variant) => variant?.image
        || card.querySelector('.ktechs-builder-card__image')?.dataset.src
        || card.querySelector('.ktechs-builder-card__image')?.currentSrc
        || '';
      const items = entries.map((entry) => ({
        id: entry.variant.id,
        quantity: entry.quantity,
        properties: {
          ...(buildLabel ? { [buildLabel]: buildId } : {}),
          _ktechs_build_id: buildId,
          ...(entry.component === 'keyboard' && cardImage(entry.card, entry.variant) ? { _ktechs_image: cardImage(entry.card, entry.variant) } : {}),
          _ktechs_builder: this.dataset.instanceLabel || 'Keyboard Builder',
          ...(this.isPackage() && this.b2b.client ? { _ktechs_client: this.b2b.client } : {}),
          _ktechs_layout: this.layoutLabels[this.layout] || '',
          _ktechs_component: entry.component,
          _ktechs_keyboard: keyboardTitle,
          ...(hasService ? {} : { _ktechs_service: 'none' }),
          ...(entry.component === 'switches' ? {
            _ktechs_required_switches: String(this.requiredSwitches()),
            _ktechs_switch_pack_size: String(entry.card.dataset.packSize || 1),
            // visible: a later cart-quantity edit contradicts this line in
            // plain sight instead of silently shorting the build (audit F45)
            [this.t('cartCountedLabel', 'Counted')]: this.tpl('cartCountedValue', { packs: entry.quantity, pack: Number(entry.card.dataset.packSize || 1), keys: this.requiredSwitches() }, '')
          } : {})
        }
      }));
      // one add call lists newest-first in the cart — reversed, the cart
      // reads keyboard → keycaps → switches → assembly top to bottom
      items.reverse();

      this.nextButton.classList.add('loading');
      this.nextButton.disabled = true;
      this.hideError();

      try {
        // Reconcile with the cart the customer already has. LOOSE lines (no
        // build id — a board carted from its page, the service ticked in the
        // drawer) fold into this build: removed here, re-added in this POST
        // with the build's properties, so the drawer shows one contiguous
        // build. Lines that belong to ANOTHER build are never touched: a board
        // already in a build is the customer's explicit choice on the review
        // step — replace that build, or add a second board — never a silent
        // theft that leaves the first build's parts orphaned (merchant, 5 Sep).
        const cart = (await this.fetchCart()) || { items: [] };
        this.cartSnapshot = cart;
        const conflict = this.cartConflict(cart);
        if (conflict && this.conflictChoice !== 'replace' && this.conflictChoice !== 'second') {
          this.updateAll(); // draws the choice on the review step
          return;
        }
        const assemblyKey = this.assemblyDefinition?.key;
        const keyboardVariant = Number(this.getVariant(this.firstSelected('keyboard'))?.id || 0);
        const chosenService = assemblyKey ? entries.find((entry) => entry.component === assemblyKey) : null;
        const serviceProduct = chosenService ? String(chosenService.card.dataset.productId || '') : '';
        const removals = [];
        cart.items.forEach((line) => {
          if (line.properties?._ktechs_build_id) return;
          if (Number(line.variant_id) === keyboardVariant) removals.push(line.key);
          else if (serviceProduct && String(line.product_id) === serviceProduct) {
            const forId = String(line.properties?._ktechs_for || '');
            if (!forId || forId === String(keyboardVariant)) removals.push(line.key);
          }
        });
        if (conflict && this.conflictChoice === 'replace') conflict.lines.forEach((line) => removals.push(line.key));
        for (const key of [...new Set(removals)]) await this.changeCartLine({ id: key, quantity: 0 });
        const toAdd = items;
        let state = {};
        if (toAdd.length) {
          const response = await fetch(window.theme?.routes?.cart_add_url || this.dataset.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: toAdd,
              sections: ['cart-drawer', 'cart-bubble'],
              sections_url: window.location.pathname
            })
          });
          state = await response.json();
          if (!response.ok || state.status) throw new Error(this.cartErrorMessage(state));
        }

        this.refreshCartSections(state.sections || {});
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: state.items?.[0] || state } }));
        this.showToast(this.t('addedToast', 'Added to cart.'));
        this.conflictChoice = null;
        this.codeUsed = true;
        this.persistLocal();
        await this.adoptCart();
        // bundle assertion (D2 plan Task 8, retail side): the cart transform
        // should have merged this build into one line. Soft on purpose: an
        // unmerged retail cart is still priced right, so never block the sale.
        const after = this.cartSnapshot;
        if (buildId && after && !after.items.some((line) => line.handle === 'ktechs-build' && String(line.properties?._ktechs_build_id || '') === String(buildId))) {
          console.warn('[kbv2] build did not merge into a bundle: cart is priced correctly but ungrouped');
        }
        const cartToggle = document.getElementById('cart-drawer-toggle');
        if (cartToggle && document.getElementById('Cart-Drawer')) cartToggle.click();
      } catch (error) {
        this.showError(error.message || this.t('errorGeneric', ''));
      } finally {
        this.nextButton.classList.remove('loading');
        this.updateActions();
      }
    }

    refreshCartSections(sections) {
      [
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ].forEach(({ id, section, selector }) => {
        const root = document.getElementById(id);
        const html = sections[section];
        if (!root || !html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        const target = root.querySelector(selector) || root;
        if (parsed && target) target.innerHTML = parsed.innerHTML;
      });
    }

    buildShareUrl() {
      // a clean canonical share URL built from state — updateUrl() edits the
      // CURRENT address (preserving foreign params) and is a no-op in the
      // theme editor, so staff copying featured-build links need this path
      const url = new URL(window.location.pathname, window.location.origin);
      if (this.codesEnabled() && this.hasBuildState()) {
        url.searchParams.set('build', this.ensureBuildCode());
        return url.toString();
      }
      if (this.layout) url.searchParams.set('builder_layout', this.layout);
      this.productDefinitions().forEach((definition) => {
        const cards = this.selectedCards(definition.key);
        const handles = cards.map((card) => card.dataset.productHandle).filter(Boolean);
        const variants = cards.map((card) => this.getVariant(card)?.id).filter(Boolean);
        if (handles.length && variants.length) {
          url.searchParams.set(`builder_${definition.key}`, handles.join(','));
          url.searchParams.set(`builder_${definition.key}_variant`, variants.join(','));
        }
      });
      return url.toString();
    }

    async copyBuildLink() {
      // a code link must resolve the moment it is pasted: push the build first,
      // and if the store cannot take it, hand out the long-form link instead
      if (this.codesEnabled() && this.hasBuildState()) await this.saveRemote();
      this.updateUrl();
      const shareUrl = window.Shopify?.designMode ? this.buildShareUrl() : window.location.href;
      try {
        await navigator.clipboard.writeText(shareUrl);
        this.showToast(this.t('shareToast', 'Link copied.'));
      } catch (_error) {
        // clipboard unavailable: at least frame the raw URL
        this.showToast(`${this.t('share', 'Share')}: ${shareUrl}`, null, { sticky: true });
      }
    }

    /* ---------- feedback ---------- */

    formatMoney(cents) {
      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(cents, this.dataset.moneyFormat || window.theme?.settings?.money_with_currency_format);
      }
      const currency = window.Shopify?.currency?.active || 'SGD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-SG', { style: 'currency', currency }).format(Number(cents || 0) / 100);
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    hideError() {
      if (this.errorBox) this.errorBox.hidden = true;
    }

    showToast(message, undoFn, opts = {}) {
      const toast = this.querySelector('[data-builder-toast]');
      if (!toast) return;
      toast.querySelector('[data-builder-toast-msg]').textContent = message;
      const srStatus = this.querySelector('[data-builder-sr-status]');
      if (srStatus) {
        // clear-then-set so repeating the same message still announces
        srStatus.textContent = '';
        setTimeout(() => { srStatus.textContent = message; }, 50);
      }
      const undoButton = toast.querySelector('[data-builder-toast-undo]');
      undoButton.hidden = !undoFn;
      undoButton.textContent = this.t('undo', 'Undo');
      undoButton.onclick = undoFn ? () => {
        // the pill hides with the toast — leave focus on the current step's
        // tab instead of dropping it to <body>
        this.focusKey = `tab:${this.stepDefinitions[this.currentStep]?.key}`;
        undoFn();
        this.hideToast();
        this.restoreFocus();
      } : null;
      toast.classList.add('is-visible');
      clearTimeout(this.toastTimer);
      this.clearToastDismiss();
      // toasts with an action (or marked sticky) persist until the customer
      // acts — a timed-only Undo is not enough (WCAG 2.2.1)
      if (undoFn || opts.sticky) {
        this.toastDismiss = (event) => { if (!toast.contains(event.target)) this.hideToast(); };
        this.addEventListener('click', this.toastDismiss, { capture: true });
      } else {
        this.toastTimer = setTimeout(() => this.hideToast(), 3200);
      }
    }

    clearToastDismiss() {
      if (!this.toastDismiss) return;
      this.removeEventListener('click', this.toastDismiss, { capture: true });
      this.toastDismiss = null;
    }

    hideToast() {
      this.clearToastDismiss();
      const toast = this.querySelector('[data-builder-toast]');
      if (!toast) return;
      // hiding while focus is inside (✕ reached by keyboard as the timer
      // fires) must not drop focus to <body> — WCAG 2.4.3
      if (toast.contains(document.activeElement)) {
        this.focusKey = `tab:${this.stepDefinitions[this.currentStep]?.key}`;
        this.restoreFocus();
      }
      toast.classList.remove('is-visible');
      toast.querySelector('[data-builder-toast-undo]').hidden = true;
    }

    escapeHtml(value) {
      const element = document.createElement('span');
      element.textContent = value == null ? '' : value;
      return element.innerHTML;
    }

    escapeAttribute(value) {
      return String(value).replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
    }
  }

  customElements.define('ktechs-builder-v2', KtechsBuilderV2);
}
